import {
  OpexImportDataSchema,
  OpexImportReviewSchema,
  OpexReviewMappingsSchema,
  type OpexImportData,
} from "./opex-import-contract.js";
import { sessionFetch as fetch } from "../../../auth/session-request.js";
import { pollFiarCatalog, type FiarCatalogOptions } from "./fiar-catalog-poll.js";
import { loadShellEnv } from "../../../env.js";
import { z } from "zod";
import {
  OpeningBookPreviewSchema,
  OpeningBookCatalogSchema,
  type OpeningBookPreview,
} from "./opening-book-contract.js";
import { planningApiErrorFromPayload } from "./errors.js";
import {
  FiarSetupCatalogSchema,
  FiarSetupResultSchema,
  type FiarSetupCatalog,
  type FiarSetupResult,
  type FiarSetupRates,
} from "./fiar-setup-contract.js";

export { PlanningApiError, planningApiFailure } from "./errors.js";
export type { PlanningApiFailure } from "./errors.js";

/**
 * Thin fetch wrapper for the AIRE planning API (I07-2). Mirrors
 * `apps/shell/web/src/modules/admin/api/mapping-client.ts` exactly, not close's CSRF/cookie
 * `ApiClient` class -- the planning API sits behind the shared Lambda authorizer expecting
 * `Authorization: Bearer <access token>` (`apps/planning/api/src/lib/planning-auth.ts`'s
 * `requirePlanningUser`), the same shape as admin's API, not close's cookie-backed session. The
 * access token is the credential because the authorizer validates `aud` against the API resource
 * audience `api://lio-aire`; an ID token is denied on that check (#120).
 *
 * Business APIs return JSON, including error bodies. Governed export endpoints return binary or
 * CSV success bodies, so `downloadPlanningExport` uses the same auth path with a download-aware
 * response parser.
 */

const API_BASE = loadShellEnv().planningApiBase;

type PlanningApiTokenProvider = () => string | undefined;

let getPlanningApiToken: PlanningApiTokenProvider = () => undefined;

export function setPlanningApiTokenProvider(provider: PlanningApiTokenProvider): void {
  getPlanningApiToken = provider;
}

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const headers: Record<string, string> = { accept: "application/json", ...extra };
  const token = getPlanningApiToken();
  if (token !== undefined && token !== "") {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

// --- Working-scenario portability -----------------------------------------------------------

export interface PortablePlanningScenarioPackage {
  readonly format: "AIRE_WORKING_SCENARIO/v1";
  readonly source: {
    readonly scenario_id: string;
    readonly scenario_name: string;
    readonly scenario_kind: "BUDGET" | "FORECAST" | "WHAT_IF";
    readonly actuals_cutover_date: string | null;
    readonly cycle_start_fiscal_year: number;
    readonly cycle_horizon_years: number;
    readonly company_codes: readonly string[];
  };
  readonly facts: readonly Record<string, unknown>[];
  readonly warnings: readonly string[];
  readonly package_checksum: string;
}

export interface PortablePlanningScenarioArchive {
  readonly format: "AIRE_WORKING_SCENARIO_ARCHIVE/v1";
  readonly encoding: "gzip+base64";
  readonly package_checksum: string;
  readonly source: {
    readonly scenario_name: string;
    readonly cycle_start_fiscal_year: number;
    readonly cycle_horizon_years: number;
  };
  readonly compressed_payload: string;
}

export type PortablePlanningScenarioTransfer =
  | PortablePlanningScenarioPackage
  | PortablePlanningScenarioArchive;

export interface PortablePlanningScenarioPreview {
  readonly status: "READY";
  readonly package_checksum: string;
  readonly source_scenario_id: string;
  readonly source_scenario_name: string;
  readonly target_cycle_id: string;
  readonly target_name: string;
  readonly company_codes: readonly string[];
  readonly fact_count: number;
  readonly warnings: readonly string[];
}

export async function exportPortablePlanningScenario(
  scenarioId: string,
): Promise<PortablePlanningScenarioTransfer> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/portable-export`,
    { headers: authHeaders() },
  );
  const payload = (await parseJsonResponse(response)) as {
    readonly package?: PortablePlanningScenarioPackage;
    readonly archive?: PortablePlanningScenarioArchive;
  };
  const transfer = payload.archive ?? payload.package;
  if (transfer === undefined) throw new Error("Planning API returned no scenario package.");
  return transfer;
}

export async function previewPortablePlanningScenario(input: {
  readonly transfer: PortablePlanningScenarioTransfer;
  readonly targetCycleId: string;
  readonly targetName: string;
}): Promise<PortablePlanningScenarioPreview> {
  const response = await fetch(`${API_BASE}/api/planning/scenario-imports/preview`, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({
      ...scenarioTransferBody(input.transfer),
      target_cycle_id: input.targetCycleId,
      target_name: input.targetName,
    }),
  });
  const payload = (await parseJsonResponse(response)) as {
    readonly preview: PortablePlanningScenarioPreview;
  };
  return payload.preview;
}

export async function applyPortablePlanningScenario(input: {
  readonly transfer: PortablePlanningScenarioTransfer;
  readonly targetCycleId: string;
  readonly targetName: string;
  readonly expectedPackageChecksum: string;
}): Promise<{
  readonly scenario_id: string;
  readonly package_checksum: string;
  readonly imported_fact_count: number;
  readonly created: boolean;
}> {
  const response = await fetch(`${API_BASE}/api/planning/scenario-imports/apply`, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({
      ...scenarioTransferBody(input.transfer),
      target_cycle_id: input.targetCycleId,
      target_name: input.targetName,
      expected_package_checksum: input.expectedPackageChecksum,
    }),
  });
  return (await parseJsonResponse(response)) as {
    readonly scenario_id: string;
    readonly package_checksum: string;
    readonly imported_fact_count: number;
    readonly created: boolean;
  };
}

function scenarioTransferBody(transfer: PortablePlanningScenarioTransfer):
  | { readonly package: PortablePlanningScenarioPackage }
  | {
      readonly archive: PortablePlanningScenarioArchive;
    } {
  return transfer.format === "AIRE_WORKING_SCENARIO_ARCHIVE/v1"
    ? { archive: transfer }
    : { package: transfer };
}

// --- UAT prototype import operations ---------------------------------------------------------

export type PrototypeImportJobStatus =
  | "PREVIEWED"
  | "APPLYING"
  | "PARTIAL_FAILED"
  | "FAILED"
  | "COMPLETE";

export interface PrototypeImportJobItemPayload {
  readonly item_id: string;
  readonly source_scenario_label: string;
  readonly target_scenario_id: string;
  readonly company_codes: readonly string[];
  readonly status: "READY" | "RUNNING" | "FAILED" | "COMPLETE";
  readonly premium_fact_count: number;
  readonly applied_fact_count: number;
  readonly changed_fact_count: number;
  readonly attempt_count: number;
  readonly error_code: string | null;
  readonly error_message: string | null;
}

export interface PrototypeImportJobPayload {
  readonly job_id: string;
  readonly source_sha256: string;
  readonly preview_checksum: string;
  readonly status: PrototypeImportJobStatus;
  readonly requested_at: string;
  readonly apply_attempt: number;
  readonly apply_requested_at: string | null;
  readonly execution_arn: string | null;
  readonly completed_at: string | null;
  readonly summary: {
    readonly canApply: boolean;
    readonly warningCount: number;
    readonly scenarioCount: number;
    readonly premiumFactCount: number;
    readonly blockingIssueCount: number;
  };
  readonly items: readonly PrototypeImportJobItemPayload[];
}

export interface PrototypeImportPreviewRequest {
  readonly sourceText: string;
  readonly options: Record<string, unknown>;
  readonly controls: {
    readonly environment: "UAT_ONLY";
    readonly placeholderUse: "UAT_ONLY/PLACEHOLDER";
    readonly prototypeVersion: string;
    readonly prototypeSha256: string;
    readonly sourceScenarioMappings: readonly unknown[];
  };
}

export async function createPrototypeImportJobPreview(
  request: PrototypeImportPreviewRequest,
): Promise<PrototypeImportJobPayload> {
  const response = await fetch(`${API_BASE}/api/planning/import-jobs/preview`, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify(request),
  });
  return (await parseJsonResponse(response)) as PrototypeImportJobPayload;
}

export async function getPrototypeImportJob(jobId: string): Promise<PrototypeImportJobPayload> {
  const response = await fetch(
    `${API_BASE}/api/planning/import-jobs/${encodeURIComponent(jobId)}`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as PrototypeImportJobPayload;
}

export async function applyPrototypeImportJob(
  jobId: string,
  previewChecksum: string,
  confirmation: string,
): Promise<PrototypeImportJobPayload> {
  const response = await fetch(
    `${API_BASE}/api/planning/import-jobs/${encodeURIComponent(jobId)}/apply`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({ previewChecksum, confirmation }),
    },
  );
  return (await parseJsonResponse(response)) as PrototypeImportJobPayload;
}

// --- Grants (I07-17, GET /api/planning/grants/me) -------------------------------------------

export type PlanCapability =
  | "ENTER"
  | "SUBMIT"
  | "REVIEW"
  | "APPROVE"
  | "VIEW"
  | "FINALIZE"
  | "UNLOCK"
  | "EXPORT"
  | "ADMINISTER";

export type PlanScopeType =
  | "DIVISION"
  | "COST_CENTER"
  | "PRODUCT"
  | "LOB"
  | "EMPLOYEE_SET"
  | "ENTITY"
  | "GLOBAL";

export interface PlanningGrantPayload {
  readonly grant_id: string;
  readonly subject_sub: string;
  readonly capability: PlanCapability;
  readonly section: string;
  readonly scope_type: PlanScopeType;
  readonly scope_key: string;
  readonly granted_at: string;
  readonly effective_from: string;
  readonly effective_to: string | null;
  readonly grant_version: number;
  readonly source: "FIXTURE" | "VAULT";
}

export async function getPlanningGrants(): Promise<{ readonly grants: PlanningGrantPayload[] }> {
  const response = await fetch(`${API_BASE}/api/planning/grants/me`, { headers: authHeaders() });
  return (await parseJsonResponse(response)) as { readonly grants: PlanningGrantPayload[] };
}

// --- Governed planning coordinate catalog ---------------------------------------------------

export interface PlanningReferenceLineOfBusiness {
  readonly code: string;
  readonly display_label: string;
  readonly active: boolean;
  readonly sort_order: number;
  readonly workday_worktag_reference_id: string | null;
  readonly workday_worktag_label: string | null;
}

export interface PlanningReferenceDivision {
  readonly code: string;
  readonly display_label: string;
  readonly active: boolean;
  readonly sort_order: number;
}

export interface PlanningReferenceProduct {
  readonly code: string;
  readonly division_code: string;
  readonly display_label: string;
  readonly product_kind: "RETAIL" | "WHOLESALE" | "PROGRAMS" | "NREIG";
  readonly active: boolean;
  readonly sort_order: number;
  readonly workday_pc_code: string | null;
  readonly workday_worktag_label: string | null;
}

/** A governed `PlanRefCompany` row (ADR-071/ADR-072). `plannable` and `reportable` are distinct
 * sets: `plannable` is the companies Finance plans (scenario company pickers use this); `reportable`
 * is the wider set financial statements consolidate over.
 *
 * `admitted` and `premium_tax_rate` are the governed premium-tax reference rate (ADR-038
 * amendment, 2026-09-08): a single rate blended across every state and LOB the entity writes.
 * `premium_tax_rate` is a fraction on the wire, matching every other rate this API carries. */
export interface PlanningReferenceCompany {
  readonly code: string;
  readonly display_label: string;
  readonly plannable: boolean;
  readonly reportable: boolean;
  readonly active: boolean;
  readonly admitted: boolean;
  readonly premium_tax_rate: number;
  readonly sort_order: number;
  readonly workday_company_code: string | null;
  readonly workday_company_wid: string | null;
  readonly workday_ledger_type_id: string | null;
  readonly workday_worktag_label: string | null;
  readonly updated_by_sub: string;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface PlanningReferenceDataResponse {
  readonly companies: readonly PlanningReferenceCompany[];
  readonly divisions: readonly PlanningReferenceDivision[];
  readonly lines_of_business: readonly PlanningReferenceLineOfBusiness[];
  readonly products: readonly PlanningReferenceProduct[];
}

/**
 * The small, admin-managed catalog ordinary planning users use for coordinate pickers. The API
 * deliberately permits every authenticated planning reader; mutation remains administrator-only.
 */
export async function getPlanningReferenceData(): Promise<PlanningReferenceDataResponse> {
  const response = await fetch(`${API_BASE}/api/planning/reference-data`, {
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as PlanningReferenceDataResponse;
}

/**
 * Full-representation PUT: the body describes the whole company row, so every capability-bearing
 * field is required, not partial -- an omitted field would silently clear it server-side (see the
 * comment on `ReferenceCompanyUpsertRequestSchema`). Admin-only; the API returns 403 for anyone
 * else, the same gate `PUT /api/planning/reference-data/{divisions,products,lines-of-business}`
 * already uses.
 */
export interface PlanningReferenceCompanyUpsertRequest {
  readonly display_label: string;
  readonly plannable: boolean;
  readonly reportable: boolean;
  readonly active: boolean;
  readonly admitted: boolean;
  readonly premium_tax_rate: number;
  readonly sort_order: number;
  readonly workday_company_code: string | null;
  readonly workday_company_wid: string | null;
  readonly workday_ledger_type_id: string | null;
  readonly workday_worktag_label: string | null;
}

export async function updatePlanningReferenceCompany(
  code: string,
  request: PlanningReferenceCompanyUpsertRequest,
): Promise<{ readonly company: PlanningReferenceCompany }> {
  const response = await fetch(
    `${API_BASE}/api/planning/reference-data/companies/${encodeURIComponent(code)}`,
    {
      method: "PUT",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(request),
    },
  );
  return (await parseJsonResponse(response)) as { readonly company: PlanningReferenceCompany };
}

export type WorkdayReferenceDimensionType =
  | "LINE_OF_BUSINESS"
  | "REVENUE_CATEGORY"
  | "SPEND_CATEGORY"
  | "ACCIDENT_YEAR"
  | "REINSURANCE_CODE"
  | "REINSURANCE_TYPE"
  | "REINSURER";

export interface PlanningWorkdayLedgerAccount {
  readonly code: string;
  readonly display_label: string;
  readonly account_name: string;
  readonly account_set: string;
  readonly account_type: string;
  readonly included_by_account_summary: string | null;
  readonly active: boolean;
  readonly source_version: string;
}

export interface PlanningWorkdayCostCenter {
  readonly reference_id: string;
  readonly code: string | null;
  readonly display_label: string;
  readonly subtype: string | null;
  readonly included_by_organizations: string | null;
  readonly worktags: string | null;
  readonly active: boolean;
  readonly source_version: string;
}

export interface PlanningWorkdayWorktag {
  readonly dimension_type: WorkdayReferenceDimensionType;
  readonly reference_id: string;
  readonly display_label: string;
  readonly active: boolean;
  readonly source_version: string;
}

export async function getPlanningWorkdayLedgerAccounts(
  activeOnly = true,
): Promise<readonly PlanningWorkdayLedgerAccount[]> {
  const query = new URLSearchParams({ active_only: String(activeOnly) });
  const response = await fetch(
    `${API_BASE}/api/planning/reference-data/workday/ledger-accounts?${query.toString()}`,
    { headers: authHeaders() },
  );
  const payload = (await parseJsonResponse(response)) as {
    readonly ledger_accounts: readonly PlanningWorkdayLedgerAccount[];
  };
  return payload.ledger_accounts;
}

export async function getPlanningWorkdayCostCenters(
  activeOnly = true,
): Promise<readonly PlanningWorkdayCostCenter[]> {
  const query = new URLSearchParams({ active_only: String(activeOnly) });
  const response = await fetch(
    `${API_BASE}/api/planning/reference-data/workday/cost-centers?${query.toString()}`,
    { headers: authHeaders() },
  );
  const payload = (await parseJsonResponse(response)) as {
    readonly cost_centers: readonly PlanningWorkdayCostCenter[];
  };
  return payload.cost_centers;
}

export async function getPlanningWorkdayWorktags(
  dimensionType?: WorkdayReferenceDimensionType,
  activeOnly = true,
): Promise<readonly PlanningWorkdayWorktag[]> {
  const query = new URLSearchParams({ active_only: String(activeOnly) });
  if (dimensionType !== undefined) query.set("dimension_type", dimensionType);
  const response = await fetch(
    `${API_BASE}/api/planning/reference-data/workday/worktags?${query.toString()}`,
    { headers: authHeaders() },
  );
  const payload = (await parseJsonResponse(response)) as {
    readonly worktags: readonly PlanningWorkdayWorktag[];
  };
  return payload.worktags;
}

export interface PlanningWorkdayReferenceCoverage {
  readonly dimension: string;
  readonly bundle_rows: number;
  readonly loaded_rows: number;
}

export interface PlanningWorkdayReferenceSeedStatus {
  readonly bundle_source_version: string;
  readonly bundle_manifest_sha256: string;
  readonly bundle_files: number;
  readonly loaded_source_version: string | null;
  readonly loaded_manifest_sha256: string | null;
  readonly loaded_at: string | null;
  readonly loaded_by_sub: string | null;
  readonly in_sync: boolean;
  readonly coverage: readonly PlanningWorkdayReferenceCoverage[];
}

export interface PlanningWorkdayReferenceSeedResult {
  readonly outcome: "APPLIED" | "ALREADY_APPLIED";
  readonly source_version: string;
  readonly manifest_sha256: string;
  readonly ledger_accounts: number;
  readonly cost_centers: number;
  readonly worktags: number;
}

export async function getPlanningWorkdayReferenceSeedStatus(): Promise<PlanningWorkdayReferenceSeedStatus> {
  const response = await fetch(`${API_BASE}/api/planning/reference-data/workday/seed-status`, {
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as PlanningWorkdayReferenceSeedStatus;
}

/**
 * Loads the deployed reference bundle into this environment.
 *
 * No request body: the bundle is the deployed artefact, never anything this client supplies. Sending
 * one would be ignored server-side, and not sending one keeps that obvious from the call site.
 */
export async function loadPlanningWorkdayReferenceSeed(): Promise<PlanningWorkdayReferenceSeedResult> {
  const response = await fetch(`${API_BASE}/api/planning/reference-data/workday/seed`, {
    method: "POST",
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as PlanningWorkdayReferenceSeedResult;
}

export interface VaultGrantPayload extends Omit<
  PlanningGrantPayload,
  "source" | "effective_from" | "effective_to" | "subject_sub"
> {
  readonly source: "VAULT";
  /** Legacy pre-ADR-064 rows only. Account-keyed rows carry `enrollment_id` instead. */
  readonly subject_sub: string | null;
  readonly enrollment_id: string | null;
  readonly user_email: string | null;
  readonly user_status: "PENDING" | "VERIFIED" | null;
  readonly effective_from: string;
  readonly effective_to: string | null;
  readonly administered_by_sub: string;
  readonly change_reason: string;
}

export interface VaultUserIdentityPayload {
  /** Stable Planning account id -- what a VAULT assignment is keyed on (ADR-064). */
  readonly account_id: string;
  readonly subject_sub: string | null;
  readonly email: string;
  readonly source: "ADMIN" | "OKTA_LOGIN";
  readonly status: "PENDING" | "VERIFIED";
  readonly added_by_sub: string | null;
  readonly added_at: string;
  readonly first_seen_at: string | null;
  readonly last_seen_at: string | null;
}

export async function addVaultUser(email: string): Promise<VaultUserIdentityPayload> {
  const response = await fetch(`${API_BASE}/api/planning/vault/users`, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ email }),
  });
  const payload = (await parseJsonResponse(response)) as { user: VaultUserIdentityPayload };
  return payload.user;
}

export async function verifyCurrentVaultUser(): Promise<VaultUserIdentityPayload> {
  const response = await fetch(`${API_BASE}/api/planning/vault/users/me/verify`, {
    method: "POST",
    headers: authHeaders(),
  });
  const payload = (await parseJsonResponse(response)) as { user: VaultUserIdentityPayload };
  return payload.user;
}

export async function listVaultUsers(
  query?: string,
): Promise<{ readonly users: VaultUserIdentityPayload[] }> {
  const suffix =
    query === undefined || query.trim() === ""
      ? ""
      : `?${new URLSearchParams({ q: query.trim() }).toString()}`;
  const response = await fetch(`${API_BASE}/api/planning/vault/users${suffix}`, {
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as { readonly users: VaultUserIdentityPayload[] };
}

export async function listVaultGrants(
  accountId?: string,
): Promise<{ readonly grants: VaultGrantPayload[]; readonly enforcement_enabled: boolean }> {
  const query =
    accountId === undefined || accountId === ""
      ? ""
      : `?${new URLSearchParams({ accountId }).toString()}`;
  const response = await fetch(`${API_BASE}/api/planning/vault/grants${query}`, {
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as {
    readonly grants: VaultGrantPayload[];
    readonly enforcement_enabled: boolean;
  };
}

export interface VaultAccessPreviewResponse {
  readonly user: VaultUserIdentityPayload;
  readonly as_of: string;
  readonly grants: readonly VaultGrantPayload[];
  readonly enforcement_enabled: boolean;
  readonly preview_mode: "READ_ONLY";
}

/**
 * Administrator-only, audited projection of one Planning account's currently effective VAULT
 * assignments. This never changes the browser's authenticated principal and never enables writes.
 */
export async function getVaultAccessPreview(
  accountId: string,
  asOf?: string,
): Promise<VaultAccessPreviewResponse> {
  const query = new URLSearchParams({ accountId });
  if (asOf !== undefined) query.set("asOf", asOf);
  const response = await fetch(
    `${API_BASE}/api/planning/vault/access-preview?${query.toString()}`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as VaultAccessPreviewResponse;
}

export async function createVaultGrant(input: {
  readonly enrollment_id: string;
  readonly capability: PlanCapability;
  readonly section: string;
  readonly scope_type: PlanScopeType;
  readonly scope_key: string;
  readonly effective_from: string;
  readonly change_reason: string;
}): Promise<{ readonly grant: VaultGrantPayload }> {
  const response = await fetch(`${API_BASE}/api/planning/vault/grants`, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify(input),
  });
  return (await parseJsonResponse(response)) as { readonly grant: VaultGrantPayload };
}

export async function createVaultGrants(input: {
  readonly enrollment_id: string;
  readonly assignments: readonly {
    readonly capability: PlanCapability;
    readonly section: string;
    readonly scope_type: PlanScopeType;
    readonly scope_key: string;
  }[];
  readonly effective_from: string;
  readonly change_reason: string;
}): Promise<{ readonly grants: VaultGrantPayload[] }> {
  const response = await fetch(`${API_BASE}/api/planning/vault/grants/batch`, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify(input),
  });
  return (await parseJsonResponse(response)) as { readonly grants: VaultGrantPayload[] };
}

export async function endDateVaultGrant(
  grantId: string,
  input: { readonly effective_to: string; readonly change_reason: string },
): Promise<{ readonly grant: VaultGrantPayload }> {
  const response = await fetch(
    `${API_BASE}/api/planning/vault/grants/${encodeURIComponent(grantId)}/end-date`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(input),
    },
  );
  return (await parseJsonResponse(response)) as { readonly grant: VaultGrantPayload };
}

export async function endDateVaultGrants(
  grantIds: readonly string[],
  input: { readonly effective_to: string; readonly change_reason: string },
): Promise<{ readonly grants: VaultGrantPayload[] }> {
  const response = await fetch(`${API_BASE}/api/planning/vault/grants/batch/end-date`, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ grant_ids: grantIds, ...input }),
  });
  return (await parseJsonResponse(response)) as { readonly grants: VaultGrantPayload[] };
}

// --- Planning cycles and scenarios (I07-4) -------------------------------------------------

export type PlanningCyclePurpose = "ANNUAL_PLAN" | "FORECAST" | "LONG_RANGE_PLAN" | "WHAT_IF";

export interface PlanningCyclePayload {
  readonly id: string;
  readonly name: string;
  readonly start_fiscal_year: number;
  readonly horizon_years: number;
  readonly purpose: PlanningCyclePurpose;
  readonly status: "ACTIVE" | "ARCHIVED";
  readonly archived_at?: string | null;
  readonly archived_by_sub?: string | null;
  readonly updated_at: string;
}

export interface PlanningScenarioPayload {
  readonly id: string;
  readonly cycle_id: string;
  readonly company_codes: readonly string[];
  readonly name: string;
  readonly scenario_kind: "BUDGET" | "FORECAST" | "WHAT_IF";
  readonly parent_scenario_id: string | null;
  readonly lifecycle_state:
    | "WORKING"
    | "SUBMITTED"
    | "IN_REVIEW"
    | "SIGNED_OFF"
    | "FORECAST_MATURING";
  readonly created_by_sub: string;
  readonly created_at: string;
  readonly actuals_cutover_date: string | null;
  readonly archived_at?: string | null;
  readonly archived_by_sub?: string | null;
}

export type PlanningCurrentCycleSummaryPayload =
  | { readonly readiness: "NO_ACTIVE_CYCLE" }
  | {
      readonly readiness: "AMBIGUOUS_ACTIVE_CYCLES";
      readonly active_cycle_count: number;
    }
  | {
      readonly readiness: "AVAILABLE";
      readonly cycle: {
        readonly id: string;
        readonly name: string;
        readonly start_fiscal_year: number;
        readonly horizon_years: number;
        readonly status: "ACTIVE";
      };
      readonly scenarios: {
        readonly total: number;
        readonly by_kind: {
          readonly BUDGET: number;
          readonly FORECAST: number;
          readonly WHAT_IF: number;
        };
        readonly by_lifecycle: {
          readonly WORKING: number;
          readonly SUBMITTED: number;
          readonly IN_REVIEW: number;
          readonly SIGNED_OFF: number;
          readonly FORECAST_MATURING: number;
        };
      };
      readonly worker_presence: { readonly readiness: "UNAVAILABLE" };
      readonly target_lock_date: { readonly readiness: "UNAVAILABLE" };
    };

export async function getPlanningCurrentCycleSummary(): Promise<PlanningCurrentCycleSummaryPayload> {
  const response = await fetch(`${API_BASE}/api/planning/dashboard/current-cycle`, {
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as PlanningCurrentCycleSummaryPayload;
}

export interface PlanningScenarioReadinessLanePayload {
  readonly section: string;
  readonly state: "NOT_STARTED" | "PARTIAL" | "COMPLETE" | "UNSUPPORTED";
  readonly horizon_months: number;
  readonly saved_months: number | null;
  readonly scope_description: string;
  readonly last_saved_at: string | null;
}

export interface PlanningScenarioReadinessPayload {
  readonly scenario_id: string;
  readonly horizon_months: number;
  readonly totals: {
    readonly saved_months: number;
    readonly possible_months: number;
    readonly input_lanes: number;
    readonly supported_lanes: number;
  };
  readonly lanes: readonly PlanningScenarioReadinessLanePayload[];
}

export async function getPlanningScenarioReadiness(
  scenarioId: string,
): Promise<PlanningScenarioReadinessPayload> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/readiness`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as PlanningScenarioReadinessPayload;
}

export async function listPlanningCycles(
  includeArchived = false,
): Promise<{ readonly cycles: PlanningCyclePayload[] }> {
  const suffix = includeArchived ? "?includeArchived=true" : "";
  const response = await fetch(`${API_BASE}/api/planning/cycles${suffix}`, {
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as { readonly cycles: PlanningCyclePayload[] };
}

export async function archivePlanningCycle(
  cycleId: string,
): Promise<{ readonly cycle: PlanningCyclePayload }> {
  const response = await fetch(
    `${API_BASE}/api/planning/cycles/${encodeURIComponent(cycleId)}/archive`,
    { method: "POST", headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as { readonly cycle: PlanningCyclePayload };
}

export async function createPlanningCycle(input: {
  readonly name: string;
  readonly startFiscalYear: number;
  readonly horizonYears: number;
  readonly purpose: PlanningCyclePurpose;
}): Promise<{ readonly cycle: PlanningCyclePayload }> {
  const response = await fetch(`${API_BASE}/api/planning/cycles`, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify(input),
  });
  return (await parseJsonResponse(response)) as { readonly cycle: PlanningCyclePayload };
}

export async function listPlanningScenarios(
  cycleId: string,
  includeArchived = false,
): Promise<{ readonly scenarios: PlanningScenarioPayload[] }> {
  const query = new URLSearchParams({ cycleId });
  if (includeArchived) query.set("includeArchived", "true");
  const response = await fetch(`${API_BASE}/api/planning/scenarios?${query.toString()}`, {
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as { readonly scenarios: PlanningScenarioPayload[] };
}

export async function archivePlanningScenario(
  scenarioId: string,
): Promise<{ readonly scenario: PlanningScenarioPayload }> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/archive`,
    { method: "POST", headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as { readonly scenario: PlanningScenarioPayload };
}

export async function deleteArchivedPlanningScenario(input: {
  scenarioId: string;
  expectedName: string;
}): Promise<{ scenario_id: string; deleted_at: string }> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(input.scenarioId)}/delete-archived`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ expectedName: input.expectedName }),
    },
  );
  return z
    .object({ scenario_id: z.string().uuid(), deleted_at: z.string().datetime() })
    .parse(await parseJsonResponse(response));
}

export async function createPlanningScenario(input: {
  readonly cycleId: string;
  readonly companyCodes: readonly string[];
  readonly name: string;
  readonly scenarioKind: PlanningScenarioPayload["scenario_kind"];
  readonly parentScenarioId?: string;
}): Promise<{ readonly scenario: PlanningScenarioPayload }> {
  const response = await fetch(`${API_BASE}/api/planning/scenarios`, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify(input),
  });
  return (await parseJsonResponse(response)) as { readonly scenario: PlanningScenarioPayload };
}

const UpdatedPlanningScenarioSchema = z.object({
  scenario: z.object({
    id: z.string().uuid(),
    cycle_id: z.string().uuid(),
    company_codes: z.array(z.string().min(1)).min(1),
    name: z.string().min(1),
    scenario_kind: z.enum(["BUDGET", "FORECAST", "WHAT_IF"]),
    parent_scenario_id: z.string().uuid().nullable(),
    lifecycle_state: z.enum([
      "WORKING",
      "SUBMITTED",
      "IN_REVIEW",
      "SIGNED_OFF",
      "FORECAST_MATURING",
    ]),
    created_by_sub: z.string(),
    created_at: z.string(),
    actuals_cutover_date: z.string().nullable(),
    archived_at: z.string().nullable().default(null),
    archived_by_sub: z.string().nullable().default(null),
  }),
});

export async function updatePlanningScenario(
  scenarioId: string,
  input: {
    readonly name: string;
    readonly companyCodes: readonly string[];
    readonly expectedName: string;
    readonly expectedCompanyCodes: readonly string[];
  },
): Promise<{ readonly scenario: PlanningScenarioPayload }> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}`,
    {
      method: "PATCH",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(input),
    },
  );
  return UpdatedPlanningScenarioSchema.parse(await parseJsonResponse(response));
}

export async function forkPlanningScenario(
  sourceScenarioId: string,
  input: {
    readonly name: string;
    readonly scenarioKind: PlanningScenarioPayload["scenario_kind"];
    readonly actualsCutoverDate?: string;
  },
): Promise<{
  readonly scenario: PlanningScenarioPayload;
  readonly fork: {
    readonly source_scenario_id: string;
    readonly inheritance_mode: "WORKING_PARENT_DELTA";
    readonly locked_source_required_for_finalization: true;
  };
}> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(sourceScenarioId)}/fork`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(input),
    },
  );
  return (await parseJsonResponse(response)) as {
    readonly scenario: PlanningScenarioPayload;
    readonly fork: {
      readonly source_scenario_id: string;
      readonly inheritance_mode: "WORKING_PARENT_DELTA";
      readonly locked_source_required_for_finalization: true;
    };
  };
}

export interface PlanningLockedVersionPayload {
  readonly id: string;
  readonly cycle_id: string;
  readonly scenario_id: string;
  readonly company_codes: readonly string[] | null;
  readonly scenario_kind?: PlanningScenarioPayload["scenario_kind"] | null;
  readonly name: string;
  readonly saved_by_sub: string;
  readonly is_final: boolean;
  readonly final_version: number | null;
  readonly supersedes_version_id: string | null;
  readonly status: "ACTIVE" | "SUPERSEDED" | "RETIRED";
  readonly saved_at: string | null;
  readonly snapshot_fact_count: number | null;
  readonly snapshot_input_fact_count: number | null;
  /**
   * The outlook assumption snapshot in force when the lock was taken -- identity only, never an
   * outlook amount (ADR-058 amendment 2026-08-26). `null` when the scenario had no published
   * outlook, including on a fork whose parent has one.
   */
  readonly outlook_assumption_snapshot_id: string | null;
  readonly outlook_assumption_checksum: string | null;
}

export async function listPlanningLockedVersions(
  cycleId: string,
): Promise<{ readonly locked_versions: PlanningLockedVersionPayload[] }> {
  const query = new URLSearchParams({ cycleId });
  const response = await fetch(`${API_BASE}/api/planning/locked-versions?${query.toString()}`, {
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as {
    readonly locked_versions: PlanningLockedVersionPayload[];
  };
}

export async function savePlanningLockedVersion(
  scenarioId: string,
  input: { readonly name: string; readonly note?: string },
): Promise<{ readonly locked_version: PlanningLockedVersionPayload }> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/locked-versions`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(input),
    },
  );
  return (await parseJsonResponse(response)) as {
    readonly locked_version: PlanningLockedVersionPayload;
  };
}

export type PlanningScenarioLifecycleAction =
  | "SUBMIT"
  | "SEND_TO_REVIEW"
  | "SIGN_OFF"
  | "REJECT"
  | "MATURE_TO_FORECAST";

export async function transitionPlanningScenarioLifecycle(
  scenarioId: string,
  action: PlanningScenarioLifecycleAction,
): Promise<{ readonly scenario: PlanningScenarioPayload }> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/lifecycle`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({ action }),
    },
  );
  return (await parseJsonResponse(response)) as { readonly scenario: PlanningScenarioPayload };
}

export async function finalizePlanningBudget(
  scenarioId: string,
  input: { readonly note?: string },
): Promise<{ readonly locked_version: PlanningLockedVersionPayload }> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/finalize`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(input),
    },
  );
  return (await parseJsonResponse(response)) as {
    readonly locked_version: PlanningLockedVersionPayload;
  };
}

export interface PlanningApprovalQueueItemPayload {
  readonly scenario_id: string;
  readonly cycle_id: string;
  readonly cycle_name: string;
  readonly company_codes: readonly string[];
  readonly scenario_name: string;
  readonly scenario_kind: PlanningScenarioPayload["scenario_kind"];
  readonly lifecycle_state: "SUBMITTED" | "IN_REVIEW";
  readonly prepared_by_sub: string;
  readonly awaiting_since: string;
  readonly input_fact_count: number;
  readonly computed_fact_count: number;
  readonly approval_chain: {
    readonly total_steps: number;
    readonly signed_off_steps: number;
    readonly pending_steps: number;
  };
}

export async function listPlanningApprovals(): Promise<{
  readonly approvals: PlanningApprovalQueueItemPayload[];
}> {
  const response = await fetch(`${API_BASE}/api/planning/approvals/queue`, {
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as {
    readonly approvals: PlanningApprovalQueueItemPayload[];
  };
}

export interface PlanningApprovalAmountSummaryPayload {
  readonly baseline_amount: number;
  readonly proposed_amount: number;
  readonly difference_amount: number;
  readonly difference_percent: number | null;
}

export interface PlanningApprovalSummaryPayload {
  readonly scenario: {
    readonly id: string;
    readonly cycle_id: string;
    readonly cycle_name: string;
    readonly company_codes: readonly string[];
    readonly name: string;
    readonly scenario_kind: PlanningScenarioPayload["scenario_kind"];
    readonly lifecycle_state: "SUBMITTED" | "IN_REVIEW";
    readonly parent_scenario_id: string | null;
    readonly prepared_by_sub: string;
  };
  readonly changed_fact_count: number;
  readonly unchanged_fact_count: number;
  readonly section_totals: ReadonlyArray<
    PlanningApprovalAmountSummaryPayload & { readonly section: string }
  >;
  readonly material_changes: ReadonlyArray<
    PlanningApprovalAmountSummaryPayload & {
      readonly section: string;
      readonly period: string;
      readonly scope_type: string;
      readonly scope_key: string;
      readonly division_code: string;
      readonly cost_center_code: string;
      readonly product_code: string;
      readonly lob_code: string;
      readonly state_code: string;
      readonly employee_ref: string;
      readonly note: string | null;
    }
  >;
}

export async function getPlanningApprovalSummary(
  scenarioId: string,
): Promise<PlanningApprovalSummaryPayload> {
  const response = await fetch(
    `${API_BASE}/api/planning/approvals/scenarios/${encodeURIComponent(scenarioId)}/summary`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as PlanningApprovalSummaryPayload;
}

export async function forkPlanningForecastFromLockedVersion(
  lockedVersionId: string,
  input: { readonly name: string; readonly actualsCutoverDate: string },
): Promise<{
  readonly scenario: PlanningScenarioPayload;
  readonly fork: {
    readonly source_locked_version_id: string;
    readonly inheritance_mode: "LOCKED_SNAPSHOT";
    readonly locked_source_required_for_finalization: false;
    readonly copied_input_fact_count: number;
    readonly copied_computed_fact_count: number;
  };
}> {
  const response = await fetch(
    `${API_BASE}/api/planning/locked-versions/${encodeURIComponent(lockedVersionId)}/fork-forecast`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(input),
    },
  );
  return (await parseJsonResponse(response)) as {
    readonly scenario: PlanningScenarioPayload;
    readonly fork: {
      readonly source_locked_version_id: string;
      readonly inheritance_mode: "LOCKED_SNAPSHOT";
      readonly locked_source_required_for_finalization: false;
      readonly copied_input_fact_count: number;
      readonly copied_computed_fact_count: number;
    };
  };
}

export interface PlanningForecastFamilyPayload {
  readonly cycle_id: string;
  readonly scenario_name: string;
  readonly actuals_cutover_date: string;
  readonly scenarios: ReadonlyArray<{
    readonly id: string;
    readonly cycle_id: string;
    readonly company_codes: readonly string[];
    readonly name: string;
    readonly scenario_kind: "FORECAST";
    readonly lifecycle_state: "WORKING";
    readonly created_by_sub: string;
    readonly created_at: string;
    readonly actuals_cutover_date: string;
    readonly source_locked_version_id: string;
    readonly copied_input_fact_count: number;
    readonly copied_computed_fact_count: number;
  }>;
}

export async function forkPlanningForecastFamily(input: {
  readonly cycleId: string;
  readonly name: string;
  readonly actualsCutoverDate: string;
  readonly entityCodes: readonly string[];
}): Promise<PlanningForecastFamilyPayload> {
  const response = await fetch(`${API_BASE}/api/planning/locked-versions/fork-forecast-family`, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify(input),
  });
  return (await parseJsonResponse(response)) as PlanningForecastFamilyPayload;
}

/**
 * One governed entity an all-entity forecast would cover, with the locked source the family fork
 * would use. `eligible === false` means the server would refuse the whole batch -- the UI shows
 * this before the user invests effort, it does not decide it.
 */
export interface PlanningForecastFamilyCoverageEntity {
  readonly entity_code: string;
  readonly eligible: boolean;
  readonly locked_version_id: string | null;
  readonly locked_version_name: string | null;
  readonly snapshot_input_fact_count: number | null;
}

export interface PlanningForecastFamilyCoveragePayload {
  readonly cycle_id: string;
  /** `null` when no financial-driver publication exists -- there is then no governed entity set. */
  readonly governed_entity_source: {
    readonly financial_driver_snapshot_id: string;
    readonly source_system: string;
    readonly source_dataset: string;
    readonly source_version: string;
    readonly source_as_of: string;
  } | null;
  readonly entities: readonly PlanningForecastFamilyCoverageEntity[];
  /**
   * The plannable entities the publication covers, before the caller's preparer scope is applied.
   * A publication carries every *reportable* entity so consolidated statements are complete;
   * only some of those are entities Finance plans.
   */
  readonly plannable_entity_codes: readonly string[];
}

export async function getPlanningForecastFamilyCoverage(
  cycleId: string,
): Promise<PlanningForecastFamilyCoveragePayload> {
  const query = new URLSearchParams({ cycleId });
  const response = await fetch(
    `${API_BASE}/api/planning/locked-versions/forecast-family-coverage?${query.toString()}`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as PlanningForecastFamilyCoveragePayload;
}

export interface PlanningOpexScenarioComparisonRow {
  readonly coordinate: OpexCoordinate;
  readonly period: string;
  readonly left_amount: number;
  readonly right_amount: number;
  readonly difference_amount: number;
  readonly difference_percent: number | null;
}

export interface PlanningFinancialScenarioComparisonRow {
  readonly coordinate: {
    readonly entity_code: string;
    readonly accounting_basis: "GAAP" | "STAT";
    readonly line: string;
  };
  readonly period: string;
  readonly left_amount: number;
  readonly right_amount: number;
  readonly difference_amount: number;
  readonly difference_percent: number | null;
}

interface PlanningScenarioComparisonBase {
  readonly left_scenario_id: string;
  readonly right_scenario_id: string;
  readonly company_codes: readonly string[];
}

export interface PlanningOpexScenarioComparisonPayload extends PlanningScenarioComparisonBase {
  readonly scope: "OPEX";
  readonly rows: readonly PlanningOpexScenarioComparisonRow[];
  readonly account_totals: readonly PlanningScenarioComparisonRollup[];
  readonly period_totals: readonly PlanningScenarioComparisonRollup[];
  readonly totals: {
    readonly left_amount: number;
    readonly right_amount: number;
    readonly difference_amount: number;
    readonly difference_percent: number | null;
  };
}

export interface PlanningFinancialScenarioComparisonPayload extends PlanningScenarioComparisonBase {
  readonly scope: "FINANCIAL";
  readonly accounting_basis: "GAAP" | "STAT";
  readonly grain: "MONTH" | "YEAR";
  readonly entity_code: string;
  readonly missing_scenario_ids: readonly string[];
  readonly sources: readonly {
    readonly scenario_id: string;
    readonly snapshot_id: string | null;
    readonly snapshot_source_version: string | null;
    readonly computed_at: string | null;
  }[];
  readonly rows: readonly PlanningFinancialScenarioComparisonRow[];
}

export type PlanningScenarioComparisonPayload =
  | PlanningOpexScenarioComparisonPayload
  | PlanningFinancialScenarioComparisonPayload;

export interface PlanningScenarioComparisonRollup {
  readonly key: string;
  readonly left_amount: number;
  readonly right_amount: number;
  readonly difference_amount: number;
  readonly difference_percent: number | null;
}

export async function comparePlanningScenarios(
  leftScenarioId: string,
  rightScenarioId: string,
  options: {
    readonly scope: "OPEX" | "FINANCIAL";
    readonly accountingBasis?: "GAAP" | "STAT";
    readonly grain?: "MONTH" | "YEAR";
    readonly entityCode?: string;
  } = { scope: "OPEX" },
): Promise<PlanningScenarioComparisonPayload> {
  const query = new URLSearchParams({
    leftScenarioId,
    rightScenarioId,
    scope: options.scope,
    ...(options.accountingBasis === undefined ? {} : { accountingBasis: options.accountingBasis }),
    ...(options.grain === undefined ? {} : { grain: options.grain }),
    ...(options.entityCode === undefined ? {} : { entityCode: options.entityCode }),
  });
  const response = await fetch(`${API_BASE}/api/planning/scenarios/compare?${query.toString()}`, {
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as PlanningScenarioComparisonPayload;
}

// --- Governed actuals overlay (I07-3) ------------------------------------------------------

export interface PlanningActualsRow {
  readonly account_code: string;
  readonly cost_center: string;
  readonly period: string;
  readonly accounting_basis: "GAAP" | "STAT" | "MANAGEMENT";
  readonly actual_amount: number;
}

export interface PlanningActualsOverlayPayload {
  readonly opex_import?: OpexImportData | undefined;
  readonly company_basis_available?: boolean | undefined;
  readonly scenario_id: string;
  readonly entity_code: string;
  readonly fiscal_year: number;
  readonly through_period: string;
  readonly accounting_basis: "GAAP" | "STAT" | "MANAGEMENT";
  readonly permission_scope: "ENTITY";
  readonly source:
    | { readonly kind: "GOVERNED_JOURNAL_LINES" }
    | {
        readonly kind: "WORKDAY_OPEX_STARTING_BASIS";
        readonly source_version: string;
        readonly source_report: string;
        readonly company_scope: "All Companies";
        readonly basis_kind: "ROLLING_12_MONTHS";
        readonly basis_start_period: string;
        readonly basis_end_period: string;
        readonly manifest_sha256: string;
      };
  readonly cache: {
    readonly hit: boolean;
    readonly ttl_seconds: number;
    readonly loaded_at: string;
  };
  readonly totals: { readonly actual_amount: number; readonly row_count: number };
  readonly actuals: readonly PlanningActualsRow[];
}

const PlanningActualsOverlaySchema = z.object({
  company_basis_available: z.boolean().optional(),
  scenario_id: z.string(),
  entity_code: z.string(),
  fiscal_year: z.number(),
  through_period: z.string(),
  accounting_basis: z.enum(["GAAP", "STAT", "MANAGEMENT"]),
  permission_scope: z.literal("ENTITY"),
  source: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("GOVERNED_JOURNAL_LINES") }),
    z.object({
      kind: z.literal("WORKDAY_OPEX_STARTING_BASIS"),
      source_version: z.string(),
      source_report: z.string(),
      company_scope: z.literal("All Companies"),
      basis_kind: z.literal("ROLLING_12_MONTHS"),
      basis_start_period: z.string(),
      basis_end_period: z.string(),
      manifest_sha256: z.string(),
    }),
  ]),
  cache: z.object({ hit: z.boolean(), ttl_seconds: z.number(), loaded_at: z.string() }),
  totals: z.object({ actual_amount: z.number().finite(), row_count: z.number().int() }),
  actuals: z.array(
    z.object({
      account_code: z.string(),
      cost_center: z.string(),
      period: z.string(),
      accounting_basis: z.enum(["GAAP", "STAT", "MANAGEMENT"]),
      actual_amount: z.number().finite(),
    }),
  ),
  opex_import: OpexImportDataSchema.optional(),
});
export async function saveOpexAccountBatch(
  scenarioId: string,
  entries: readonly SaveOpexInputFactRequest[],
  expectedEmptyAccount: boolean,
) {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/opex-input-facts/batch`,
    {
      method: "PUT",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({ entries, expectedEmptyAccount }),
    },
  );
  return z
    .object({
      results: z.array(
        z.object({
          coordinate: z
            .object({
              entityCode: z.string().optional(),
              costCenterCode: z.string(),
              accountCode: z.string(),
              spendCategoryCode: z.string(),
              supplierCode: z.string().optional(),
            })
            .transform(
              (c): OpexCoordinate => ({
                costCenterCode: c.costCenterCode,
                accountCode: c.accountCode,
                spendCategoryCode: c.spendCategoryCode,
                ...(c.entityCode ? { entityCode: c.entityCode } : {}),
                ...(c.supplierCode ? { supplierCode: c.supplierCode } : {}),
              }),
            ),
          lines: z.array(z.object({ period: z.string() })),
        }),
      ),
    })
    .parse(await parseJsonResponse(response));
}
export async function getPlanningOpexImportStatus() {
  const response = await fetch(`${API_BASE}/api/planning/reference-data/workday/opex-import`, {
    headers: authHeaders(),
  });
  return z
    .object({
      latest: z
        .object({
          publication: z.object({
            revision: z.string(),
            bundle: z.object({ sourceVersion: z.string() }).passthrough(),
            mappings: OpexReviewMappingsSchema.optional(),
            supplierMatchMode: z.literal("EXACT_NAME_V1").optional(),
          }),
          publishedAt: z.string(),
          publishedBy: z.string(),
        })
        .nullable(),
    })
    .parse(await parseJsonResponse(response));
}
export async function reviewPlanningOpexImport(action: "preview" | "publish", request: unknown) {
  const response = await fetch(
    `${API_BASE}/api/planning/reference-data/workday/opex-import/${action}`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(request),
    },
  );
  return OpexImportReviewSchema.parse(await parseJsonResponse(response));
}

export async function getPlanningActualsOverlay(
  scenarioId: string,
  input: {
    readonly entityCode?: string;
    readonly throughPeriod: string;
    readonly refresh?: boolean;
    readonly purpose?: "ACTUALS_OVERLAY" | "OPEX_STARTING_BASIS";
  },
): Promise<PlanningActualsOverlayPayload> {
  const query = new URLSearchParams({
    throughPeriod: input.throughPeriod,
    accountingBasis: "GAAP",
    ...(input.entityCode === undefined ? {} : { entityCode: input.entityCode }),
    ...(input.purpose === undefined ? {} : { purpose: input.purpose }),
    ...(input.refresh === true ? { refresh: "true" } : {}),
  });
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/actuals?${query.toString()}`,
    { headers: authHeaders() },
  );
  const payload = await parseJsonResponse(response);
  return PlanningActualsOverlaySchema.parse(payload);
}

// --- Plan-to-actual variance (I07-5) -------------------------------------------------------

export interface PlanningVarianceRow {
  readonly account_code: string;
  readonly cost_center_code: string;
  readonly entity_code: string;
  readonly period: string;
  readonly accounting_basis: "GAAP" | "STAT" | "MANAGEMENT";
  readonly planned_amount: number;
  readonly actual_amount: number;
  readonly variance_amount: number;
  readonly variance_pct: number | null;
  readonly prior_period_variance_pct: number | null;
  readonly drill_back_link: string | null;
  readonly batch_ids: readonly string[];
}

export interface PlanningVarianceGridPayload {
  readonly locked_version: {
    readonly id: string;
    readonly name: string;
    readonly entity_code: string;
    readonly scenario_kind: "BUDGET" | "FORECAST";
    readonly status: "ACTIVE" | "SUPERSEDED";
  };
  readonly accounting_basis: "GAAP" | "STAT" | "MANAGEMENT";
  readonly period_start: string;
  readonly period_end: string;
  readonly coverage: {
    readonly planning_source: "LOCKED_INPUT_FACT";
    readonly included_section: "OPERATING_EXPENSE";
    readonly coordinate_mapping: "opex-coordinate-v1";
    readonly unmapped_computed_fact_count: number;
    readonly note: string;
  };
  readonly cache: {
    readonly hit: boolean;
    readonly ttl_seconds: number;
    readonly loaded_at: string;
  };
  readonly rows: readonly PlanningVarianceRow[];
  readonly pagination: {
    readonly page_size: number;
    readonly returned_rows: number;
    readonly total_rows: number;
    readonly returned_matrix_rows: number;
    readonly total_matrix_rows: number;
    readonly next_page_token: string | null;
  };
}

export interface PlanningVarianceQueryInput {
  readonly versionId: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly accountingBasis: "GAAP" | "STAT" | "MANAGEMENT";
  readonly accountFilter?: string;
  readonly costCenterFilter?: string;
  readonly varianceThresholdPct: number;
  readonly sortBy: "account_code" | "variance_amount" | "variance_pct";
  readonly sortDirection: "asc" | "desc";
  readonly pageToken?: string;
  readonly refresh?: boolean;
}

export async function getPlanningVarianceGrid(
  input: PlanningVarianceQueryInput,
): Promise<PlanningVarianceGridPayload> {
  const query = planningVarianceQuery(input);
  const response = await fetch(`${API_BASE}/api/planning/variance/grid?${query.toString()}`, {
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as PlanningVarianceGridPayload;
}

export async function downloadPlanningVarianceExport(
  input: Omit<PlanningVarianceQueryInput, "pageToken" | "refresh"> & {
    readonly format: "csv" | "xlsx";
  },
): Promise<PlanningExportDownload> {
  const query = planningVarianceQuery(input);
  query.set("format", input.format);
  const response = await fetch(`${API_BASE}/api/planning/variance/grid?${query.toString()}`, {
    headers: authHeaders({
      accept:
        input.format === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "text/csv",
    }),
  });
  if (!response.ok) {
    await parseJsonResponse(response);
    throw new Error(`Planning variance export failed with ${response.status}`);
  }
  const fallback = `planning-variance-${input.periodStart}-${input.periodEnd}.${input.format}`;
  return {
    blob: await response.blob(),
    filename: downloadFilename(response.headers.get("content-disposition"), fallback),
    contentType: response.headers.get("content-type") ?? "application/octet-stream",
  };
}

function planningVarianceQuery(input: PlanningVarianceQueryInput): URLSearchParams {
  const query = new URLSearchParams({
    versionId: input.versionId,
    periodStart: input.periodStart,
    periodEnd: input.periodEnd,
    accountingBasis: input.accountingBasis,
    varianceThresholdPct: String(input.varianceThresholdPct),
    sortBy: input.sortBy,
    sortDirection: input.sortDirection,
    ...(input.accountFilter === undefined || input.accountFilter.trim() === ""
      ? {}
      : { accountFilter: input.accountFilter.trim() }),
    ...(input.costCenterFilter === undefined || input.costCenterFilter.trim() === ""
      ? {}
      : { costCenterFilter: input.costCenterFilter.trim() }),
    ...(input.pageToken === undefined ? {} : { pageToken: input.pageToken }),
    ...(input.refresh === true ? { refresh: "true" } : {}),
  });
  return query;
}

// --- Recompute (I07-10, POST /api/planning/scenarios/{id}/recompute) ------------------------

export interface RecomputeChangedCoordinate {
  readonly coordinate: string;
  readonly period: string;
}

export interface RecomputedFactPayload {
  readonly node_id: string;
  readonly period: string;
  readonly value: number;
  readonly formula_version: string;
  readonly coordinate: {
    readonly line: string;
    readonly product_code: string;
    readonly lob_code: string;
    readonly division_code: string;
    readonly entity_code: string;
    readonly state_code: string;
    readonly gross_ceded_net: string;
    readonly reinsurance_code: string;
    readonly reinsurance_type: string;
    readonly accounting_basis: string;
  };
}

export interface RecomputeResponsePayload {
  readonly scenario_id: string;
  readonly period: string;
  readonly changed_facts: readonly RecomputedFactPayload[];
  readonly validation_outcomes: readonly {
    readonly node_id: string;
    readonly period: string;
    readonly code: string;
    readonly message: string;
  }[];
}

export async function recomputeScenario(
  scenarioId: string,
  input: { readonly period: string; readonly changed: readonly RecomputeChangedCoordinate[] },
): Promise<RecomputeResponsePayload> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/recompute`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(input),
    },
  );
  return (await parseJsonResponse(response)) as RecomputeResponsePayload;
}

// --- Operating Expense input facts (I07-2) ---------------------------------------------------

export interface OpexCoordinate {
  readonly entityCode?: string;
  readonly costCenterCode: string;
  readonly accountCode: string;
  readonly spendCategoryCode: string;
  readonly supplierCode?: string;
}

export type OpexEntry =
  | {
      readonly entryMethod: "ANNUAL_SPREAD";
      readonly fiscalYear: number;
      readonly annualAmount: number;
    }
  | {
      readonly entryMethod: "BY_MONTH";
      readonly fiscalYear: number;
      readonly monthlyAmounts: readonly number[];
    }
  | {
      readonly entryMethod: "CONTRACT_TERM";
      readonly totalAmount: number;
      readonly startPeriod: string;
      readonly endPeriod: string;
    };

export interface SaveOpexInputFactRequest {
  readonly coordinate: OpexCoordinate;
  readonly entry: OpexEntry;
  readonly note?: string;
  readonly changeSource?: "MANUAL_ENTRY" | "BULK_PASTE" | "RESEED";
}

export interface SaveOpexInputFactResponse {
  readonly scenario_id: string;
  readonly coordinate: OpexCoordinate;
  readonly entry_method: OpexEntry["entryMethod"];
  readonly lines: readonly { readonly period: string; readonly amount: number }[];
  readonly truncated_at_horizon: boolean;
  readonly saved_at: string;
}

export interface OpexInputFactRow {
  readonly id: string;
  readonly coordinate: OpexCoordinate;
  readonly period: string;
  readonly amount: number;
  readonly note: string | null;
  readonly entry_method: OpexEntry["entryMethod"] | null;
  readonly source_amount: number | null;
  readonly source_start_period: string | null;
  readonly source_end_period: string | null;
  readonly truncated_at_horizon: boolean;
  readonly entered_by_sub: string;
  readonly entered_at: string;
}

export interface ListOpexInputFactsResponse {
  readonly scenario_id: string;
  readonly facts: readonly OpexInputFactRow[];
}

export async function listOpexInputFacts(
  scenarioId: string,
  entityCode?: string,
): Promise<ListOpexInputFactsResponse> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/opex-input-facts${entityCode === undefined ? "" : `?${new URLSearchParams({ entityCode })}`}`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as ListOpexInputFactsResponse;
}

export async function saveOpexInputFact(
  scenarioId: string,
  request: SaveOpexInputFactRequest,
): Promise<SaveOpexInputFactResponse> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/opex-input-facts`,
    {
      method: "PUT",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(request),
    },
  );
  return (await parseJsonResponse(response)) as SaveOpexInputFactResponse;
}

export interface RemoveOpexSupplierResponse {
  readonly scenario_id: string;
  readonly coordinate: OpexCoordinate;
  readonly removed_years: readonly number[];
  readonly removed_periods: readonly string[];
  readonly skipped_read_only_periods: readonly string[];
  readonly removed_at: string;
}

/**
 * Removes one vendor row from the OpEx template -- UAT 2026-08-31. Shawn Trzecinski: "right now I
 * can add vendors, but I can't delete them." `coordinate.supplierCode` is required: this cannot
 * remove an account's unassigned bucket, only a named supplier.
 *
 * "Remove" zeroes and tags every period the supplier carries an entry for, across every year --
 * see `removeOpexSupplier` in `handlers/opex-input-facts.ts` for why this is not a row delete.
 */
export async function removeOpexSupplier(
  scenarioId: string,
  coordinate: OpexCoordinate & { readonly supplierCode: string },
): Promise<RemoveOpexSupplierResponse> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/opex-input-facts/remove-supplier`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({ coordinate }),
    },
  );
  return (await parseJsonResponse(response)) as RemoveOpexSupplierResponse;
}

// --- Two-stage Operating Expense allocation (I07-14) --------------------------------------

export type OpexAllocationMetric = "GWP" | "NWP" | "NEP";

export interface OpexAllocationPolicySnapshotSummaryPayload {
  readonly id: string;
  readonly policy_id: string;
  readonly policy_version: string;
  readonly effective_period: string;
  readonly expiration_period: string;
  readonly stage_two_metric: OpexAllocationMetric;
  readonly source_system: string;
  readonly source_dataset: string;
  readonly source_version: string;
  readonly source_as_of: string;
  readonly policy_checksum: string;
  readonly created_by_sub: string;
  readonly created_at: string;
}

export interface OpexAllocationPolicySnapshotPayload extends OpexAllocationPolicySnapshotSummaryPayload {
  readonly policy: {
    readonly stageOneRules: readonly {
      readonly costCenterCode: string;
      readonly allocation:
        | {
            readonly method: "DIRECT_PERCENT";
            readonly shares: readonly {
              readonly divisionCode: "RETAIL" | "WHOLESALE" | "PROGRAMS";
              readonly share: number;
            }[];
          }
        | {
            readonly method: "PREMIUM_BASIS";
            readonly metric: OpexAllocationMetric;
          };
    }[];
    readonly requiredPoolComponents: readonly ["OPERATING_EXPENSE", "COMPENSATION"];
  };
}

export interface OpexAllocationReportFactPayload {
  readonly fact_kind: "STAGE_ONE" | "PRODUCT" | "MSC_OFFSET";
  readonly line: string;
  readonly period: string;
  readonly entity_code: string;
  readonly service_entity_code: string;
  readonly cost_center_code: string;
  readonly division_code: "RETAIL" | "WHOLESALE" | "PROGRAMS" | "";
  readonly product_code: string;
  readonly amount: number;
  readonly allocation_share: number;
  readonly allocation_method: "DIRECT_PERCENT" | "PREMIUM_BASIS" | "NA";
  readonly allocation_metric: OpexAllocationMetric | "NA";
  readonly source_fact_ids: readonly string[];
  readonly source_versions: readonly string[];
  readonly source_cost_center_codes: readonly string[];
  readonly metric_formula_versions: readonly string[];
  readonly metric_source_versions: readonly string[];
  readonly formula_version: string;
  readonly computed_at: string;
}

export interface OpexAllocationReportResponse {
  readonly scenario_id: string;
  readonly policy_snapshot: {
    readonly id: string;
    readonly policy_id: string;
    readonly policy_version: string;
    readonly source_version: string;
    readonly stage_two_metric: OpexAllocationMetric;
  } | null;
  readonly facts: readonly OpexAllocationReportFactPayload[];
  readonly controls: readonly {
    readonly control_type:
      | "STAGE_ONE"
      | "STAGE_TWO"
      | "MSC_ZERO"
      | "CONSOLIDATED_ALLOCATION"
      | "CONSOLIDATED_MSC_ZERO";
    readonly period: string;
    readonly entity_code: string;
    readonly scope_code: string;
    readonly source_amount: number;
    readonly allocated_amount: number;
    readonly difference: 0;
    readonly computed_at: string;
  }[];
  readonly totals: {
    readonly source_pool: number;
    readonly product_allocated: number;
    readonly msc_offset: number;
    readonly conservation_difference: 0;
    readonly msc_zero_difference: 0;
    readonly by_entity: readonly {
      readonly entity_code: string;
      readonly source_pool: number;
      readonly product_allocated: number;
      readonly msc_offset: number;
      readonly conservation_difference: 0;
      readonly msc_zero_difference: 0;
    }[];
  };
}

export async function listOpexAllocationPolicySnapshots(): Promise<{
  readonly snapshots: readonly OpexAllocationPolicySnapshotSummaryPayload[];
}> {
  const response = await fetch(`${API_BASE}/api/planning/opex-allocation/policy-snapshots`, {
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as {
    readonly snapshots: readonly OpexAllocationPolicySnapshotSummaryPayload[];
  };
}

export async function getOpexAllocationPolicySnapshot(
  snapshotId: string,
): Promise<OpexAllocationPolicySnapshotPayload> {
  const response = await fetch(
    `${API_BASE}/api/planning/opex-allocation/policy-snapshots/${encodeURIComponent(snapshotId)}`,
    { headers: authHeaders() },
  );
  const payload = (await parseJsonResponse(response)) as {
    readonly snapshot: OpexAllocationPolicySnapshotPayload;
  };
  return payload.snapshot;
}

/**
 * Publishes an OpEx allocation policy.
 *
 * The `PUT` endpoint has existed since the allocation engine landed, but no client function called
 * it — so the policy was readable in the UI and writable only by an engineer. That is the PG-07 gap
 * for this family, and it is why the allocation basis has sat on a placeholder: there was no way for
 * Finance to change it.
 *
 * Server-side it requires Finance admin, all-entity Planning preparer scope and a global ENTER
 * grant, so this is deliberately not wired into the planning lanes — only the admin surface.
 */
export async function publishOpexAllocationPolicy(
  policy: unknown,
): Promise<OpexAllocationPolicySnapshotSummaryPayload> {
  const response = await fetch(`${API_BASE}/api/planning/opex-allocation/policy-snapshots`, {
    method: "PUT",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ policy }),
  });
  const payload = (await parseJsonResponse(response)) as {
    readonly snapshot: OpexAllocationPolicySnapshotSummaryPayload;
  };
  return payload.snapshot;
}

export async function recomputeOpexAllocation(
  scenarioId: string,
  policySnapshotId: string,
): Promise<{
  readonly company_codes: readonly string[];
  readonly stage_one_fact_count: number;
  readonly product_fact_count: number;
  readonly offset_fact_count: number;
  readonly control_count: number;
}> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/opex-allocation/recompute`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({ policy_snapshot_id: policySnapshotId }),
    },
  );
  return (await parseJsonResponse(response)) as {
    readonly company_codes: readonly string[];
    readonly stage_one_fact_count: number;
    readonly product_fact_count: number;
    readonly offset_fact_count: number;
    readonly control_count: number;
  };
}

export async function getOpexAllocationReport(
  scenarioId: string,
  periodFrom?: string,
  periodTo?: string,
): Promise<OpexAllocationReportResponse> {
  const query = new URLSearchParams();
  if (periodFrom !== undefined) query.set("period_from", periodFrom);
  if (periodTo !== undefined) query.set("period_to", periodTo);
  const suffix = query.size === 0 ? "" : `?${query.toString()}`;
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/opex-allocation/report${suffix}`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as OpexAllocationReportResponse;
}

// --- Underwriting production + premium + earning (I07-11) -----------------------------------

export type PremiumProductKind = "RETAIL" | "WHOLESALE" | "PROGRAMS" | "NREIG";
export interface PremiumCoordinate {
  readonly companyCode: string;
  readonly divisionCode: string;
  readonly productCode: string;
}
export interface PremiumLobMix {
  readonly lobCode: string;
  readonly proportion: number;
  readonly commissionRateOverride?: number;
}
/**
 * `premiumTaxRate` is READ-ONLY on this type in practice: it is present on a response the server
 * sends back (resolved server-side from the governed `PlanRefCompany` reference rate, ADR-038
 * amendment 2026-09-08) but must never be SET on a request -- the API's request schema no longer
 * accepts the field at all and a `.strict()` object rejects it with a 400. Kept optional here
 * rather than split into separate request/response types because every caller in this module reads
 * and writes through the one shape; `buildInput` in `PremiumLane.tsx` simply never populates it.
 */
export type PremiumProductionInput =
  | {
      readonly kind: "RETAIL" | "WHOLESALE";
      readonly expiringPremium: number;
      readonly expiringPremiumMode?: "DIRECT" | "PRIOR_YEAR_RENEWABLE";
      readonly expiringPremiumOverride?: number;
      readonly renewalRate: number;
      readonly rateChange: number;
      readonly submissions: number;
      readonly quoteRate: number;
      readonly hitRate: number;
      readonly averageGwp: number;
      readonly newBusinessPremiumOverride?: number;
      readonly auditRate: number;
      readonly grossCommissionRate: number;
      readonly premiumTaxRate?: number;
      readonly feeRate?: number;
    }
  | {
      readonly kind: "PROGRAMS" | "NREIG";
      readonly renewalPremium: number;
      readonly newBusinessPremium: number;
      readonly grossCommissionRate: number;
      readonly premiumTaxRate?: number;
    };
export interface PremiumUatReviewRow {
  readonly dimension: "LOB" | "STATE";
  readonly company: string;
  readonly division: string;
  readonly product: string;
  readonly member: string;
  readonly premium: string;
  readonly commission: string | null;
  readonly share: string | null;
  readonly effectiveCommissionRate: string | null;
}
export interface PremiumStateMix {
  readonly stateCode: string;
  readonly proportion: number;
}
export interface PremiumEntry {
  readonly run_off?: boolean;
  readonly zero_premium_run_off?: boolean;
  readonly state_mix?: readonly PremiumStateMix[];
  readonly coordinate: PremiumCoordinate;
  readonly period: string;
  readonly input: PremiumProductionInput;
  readonly lob_mix: readonly PremiumLobMix[];
}
export interface PremiumComputedFact {
  readonly line: string;
  readonly period: string;
  readonly company_code: string;
  readonly division_code: string;
  readonly product_code: string;
  readonly lob_code: string;
  readonly amount: number;
  readonly formula_version: "finance-blueprint-premium-earning-v1";
}
export interface SavePremiumInputFactResponse {
  readonly scenario_id: string;
  readonly coordinate: PremiumCoordinate;
  readonly period: string;
  readonly input: PremiumProductionInput;
  readonly lob_mix: readonly PremiumLobMix[];
  readonly affected_periods: readonly string[];
  readonly affected_written_periods: readonly string[];
  readonly horizon_truncated: boolean;
  readonly computed_facts: readonly PremiumComputedFact[];
  readonly saved_at: string;
}

const ProductRunOffStatusResponseSchema = z.object({
  scenario_id: z.string(),
  period_from: z.string(),
  period_to: z.string(),
  read_only_reason: z.string().nullable(),
  products: z.array(
    z.object({
      coordinate: z.object({
        companyCode: z.string(),
        divisionCode: z.string(),
        productCode: z.string(),
      }),
      product_label: z.string(),
      status: z.enum(["NOT_SET", "WRITING", "RUN_OFF", "ZERO_PREMIUM_RUN_OFF", "MIXED"]),
      saved_months: z.number().int().nonnegative(),
      run_off_months: z.number().int().nonnegative(),
      total_months: z.number().int().positive(),
      can_mark_run_off: z.boolean(),
    }),
  ),
});
export type ProductRunOffStatusResponse = z.infer<typeof ProductRunOffStatusResponseSchema>;

export async function getProductRunOffStatus(
  scenarioId: string,
): Promise<ProductRunOffStatusResponse> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/premium-input-facts/run-off`,
    { headers: authHeaders() },
  );
  return ProductRunOffStatusResponseSchema.parse(await parseJsonResponse(response));
}

export async function markPremiumRunOff(
  scenarioId: string,
  coordinate: PremiumCoordinate,
  confirmZeroOpeningPremium = false,
) {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/premium-input-facts/run-off`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ coordinate, confirmZeroOpeningPremium }),
    },
  );
  return z
    .object({ scenario_id: z.string(), month_count: z.number().int(), run_off: z.literal(true) })
    .parse(await parseJsonResponse(response));
}
export interface PremiumReportSummary {
  readonly grain: "MONTH" | "QUARTER" | "YEAR" | "HORIZON";
  readonly company_code: string;
  readonly period_key: string;
  readonly line: string;
  readonly amount: number;
}
export interface ListPremiumInputFactsResponse {
  readonly review_rows?: readonly PremiumUatReviewRow[];
  readonly scenario_id: string;
  readonly entries: readonly PremiumEntry[];
  readonly opening_cohorts?: readonly {
    readonly coordinate: PremiumCoordinate;
    readonly period: string;
    readonly lob_code: string;
    readonly baseline_written_premium: number;
    readonly written_premium_adjustment: number;
    readonly effective_written_premium: number;
    readonly source_fact_ids: readonly string[];
  }[];
  readonly computed_facts: readonly PremiumComputedFact[];
  readonly gross_earned_premium_readiness: {
    readonly ready: boolean;
    readonly has_persisted_facts: boolean;
    readonly incomplete_coordinates: readonly {
      readonly company_code: string;
      readonly division_code: string;
      readonly product_code: string;
      readonly missing_current_periods: readonly string[];
      readonly missing_prior_cohort_periods: readonly string[];
    }[];
  };
  readonly summaries: readonly PremiumReportSummary[];
}
export type PremiumInputFactsReadView =
  | {
      readonly view: "coordinate";
      readonly companyCode: string;
      readonly divisionCode: string;
      readonly productCode: string;
      readonly year: number;
    }
  | {
      readonly view: "division";
      readonly companyCode: string;
      readonly divisionCode: string;
      readonly entryYear: number;
      readonly periodFrom: string;
      readonly periodTo: string;
    }
  | { readonly view: "readiness" | "loss-readiness" };
export interface PremiumEarningsTriangleResponse {
  readonly scenario_id: string;
  readonly coordinate: PremiumCoordinate & { readonly year: number };
  readonly formula_version: "finance-blueprint-premium-earning-v1";
  readonly status:
    | "READY"
    | "MISSING_CURRENT_YEAR_INPUTS"
    | "MISSING_PRIOR_YEAR_COHORT"
    | "MISSING_CURRENT_AND_PRIOR_INPUTS";
  readonly earning_periods: readonly string[];
  readonly missing_current_periods: readonly string[];
  readonly missing_prior_cohort_periods: readonly string[];
  readonly prior_cohort_required: boolean;
  readonly views: readonly {
    readonly lob_code: string | null;
    readonly rows: readonly {
      readonly cohort_period: string;
      readonly cells: readonly { readonly earning_period: string; readonly amount: number }[];
      readonly total: number;
      readonly source_fact_ids: readonly string[];
    }[];
    readonly column_totals: readonly {
      readonly earning_period: string;
      readonly amount: number;
    }[];
    readonly triangle_total: number;
    readonly persisted_gross_earned_premium_total: number;
    readonly difference: number;
    readonly balanced: boolean;
  }[];
}
export interface PremiumYearPreviewResponse {
  readonly scenario_id: string;
  readonly persisted: false;
  readonly formula_version: "finance-blueprint-premium-earning-v1";
  readonly months: readonly {
    readonly period: string;
    readonly bound_policy_count: number;
    readonly renewal_premium: number;
    readonly new_business_premium: number;
    readonly audit_premium: number;
    readonly total_gwp: number;
    readonly gross_commission: number;
    readonly premium_tax: number;
    readonly fee_income: number;
  }[];
}
export interface FiarPlanningSeedPreviewResponse {
  readonly scenario_id: string;
  readonly idempotency_key: string;
  readonly accepted: readonly (PremiumEntry & {
    readonly entity_code: string;
    readonly state_mix: readonly { readonly stateCode: string; readonly proportion: number }[];
    readonly lineage: {
      readonly source: "FIAR";
      readonly datasetId: string;
      readonly extractedAt: string;
      readonly asOfDate: string;
      readonly mappingVersion: string;
      readonly sourceRecordIds: readonly string[];
    };
  })[];
  readonly quarantined: readonly {
    readonly source_record_ids: readonly string[];
    readonly code: string;
    readonly message: string;
  }[];
  readonly reconciliation: {
    readonly declaredRowCount: number;
    readonly observedRowCount: number;
    readonly validRowCount: number;
    readonly acceptedRowCount: number;
    readonly quarantinedRowCount: number;
    readonly declaredMonetaryTotal: number;
    readonly observedMonetaryTotal: number;
    readonly acceptedMonetaryTotal: number;
    readonly rowCountDifference: number;
    readonly monetaryDifference: number;
    readonly balanced: boolean;
  };
  readonly persisted: false;
}

export interface FiarPriorYearBasisMonth {
  readonly basis_period: string;
  readonly target_period: string;
  readonly source_kind: "ACTUAL" | "PLAN";
  readonly new_business_premium: number;
  readonly renewal_premium: number;
  readonly written_gwp: number;
  readonly submissions: number;
  readonly quote_count: number;
  readonly bound_policy_count: number;
  readonly quote_rate: number;
  readonly hit_rate: number;
  readonly average_gwp: number;
  readonly driver_derivation: "FIAR_COUNTS" | "EXISTING_RATES" | "DIRECT_PREMIUM";
  readonly source_quote_count: number;
  readonly source_bound_policy_count: number;
  readonly source_row_count: number;
  readonly driver_source_row_count: number;
  readonly source_checksum: string;
  readonly source_as_of: string;
}

export interface FiarPriorYearBasisResponse {
  readonly state_mix_warning?: string;
  readonly state_mix_basis?: {
    readonly dataset: string;
    readonly basisPeriod: string;
    readonly sourceChecksum: string;
    readonly sourceRowCount: number;
    readonly members: readonly PremiumStateMix[];
  };
  readonly job_id: string;
  readonly scenario_id: string;
  readonly source: "FIAR";
  readonly dataset_id: "FIAR_DW.RPT.TAB_EXECUTIVE_SUMMARY_LIVE";
  readonly driver_dataset_id: "FIAR_DW.RPT.TAB_LIO_METRICS_SUMMARY";
  readonly lob_mix_dataset_id:
    | "FIAR_DW.RPT.V_AIRE_PLANNING_LOB_MIX_BASIS"
    | "AIRE_PLANNING_SEED.PREMIUM_LOB_MIX_DECLARED";
  readonly mapping_version: "fiar-planning-prior-year-basis/v9";
  readonly company_code: string;
  readonly division_code: string;
  readonly product_code: string;
  readonly source_product: string;
  readonly basis_year: number;
  readonly target_year: number;
  readonly actuals_through_period: string;
  readonly lob_mix_basis_period: string;
  readonly lob_mix_method: "R12_ECONOMIC_GWP" | "FINANCE_DECLARED";
  readonly lob_mix: readonly {
    readonly lob_code: string;
    readonly proportion: number;
  }[];
  readonly lob_mix_source_row_count: number;
  readonly lob_mix_source_checksum: string;
  readonly lob_mix_source_as_of: string;
  readonly months: readonly FiarPriorYearBasisMonth[];
  readonly totals: {
    readonly written_gwp: number;
    readonly new_business_premium: number;
    readonly renewal_premium: number;
    readonly submissions: number;
    readonly quote_count: number;
    readonly bound_policy_count: number;
    readonly source_row_count: number;
    readonly driver_source_row_count: number;
    readonly month_count: number;
    readonly actual_month_count: number;
    readonly plan_month_count: number;
  };
  readonly missing_months: readonly string[];
  readonly complete: boolean;
  readonly loaded_at: string;
  readonly persisted: false;
}

interface FiarPriorYearBasisJobResponse {
  readonly job_id: string;
  readonly status: "QUEUED" | "RUNNING";
  readonly retry_after_ms: number;
  readonly cached: false;
}

export async function listPremiumInputFacts(
  scenarioId: string,
  readView: PremiumInputFactsReadView,
): Promise<ListPremiumInputFactsResponse> {
  const query = new URLSearchParams(
    Object.entries(readView).map(([key, value]) => [key, String(value)]),
  );
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/premium-input-facts?${query.toString()}`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as ListPremiumInputFactsResponse;
}

export async function getPremiumEarningsTriangle(
  scenarioId: string,
  coordinate: PremiumCoordinate & { readonly year: number },
): Promise<PremiumEarningsTriangleResponse> {
  const query = new URLSearchParams({
    companyCode: coordinate.companyCode,
    divisionCode: coordinate.divisionCode,
    productCode: coordinate.productCode,
    year: String(coordinate.year),
  });
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/premium-input-facts/earnings-triangle?${query.toString()}`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as PremiumEarningsTriangleResponse;
}

export async function downloadPremiumEarningsTriangle(
  scenarioId: string,
  coordinate: PremiumCoordinate & { readonly year: number },
  lobCode?: string,
): Promise<PlanningExportDownload> {
  const query = new URLSearchParams({
    companyCode: coordinate.companyCode,
    divisionCode: coordinate.divisionCode,
    productCode: coordinate.productCode,
    year: String(coordinate.year),
    format: "xlsx",
    ...(lobCode === undefined || lobCode === "" ? {} : { lobCode }),
  });
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/premium-input-facts/earnings-triangle?${query.toString()}`,
    {
      headers: authHeaders({
        accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
    },
  );
  if (!response.ok) {
    await parseJsonResponse(response);
    throw new Error(`Premium earnings-triangle export failed with ${response.status}`);
  }
  const fallback = `aire-earnings-triangle-${coordinate.companyCode.toLowerCase()}-${coordinate.year}.xlsx`;
  return {
    blob: await response.blob(),
    filename: downloadFilename(response.headers.get("content-disposition"), fallback),
    contentType: response.headers.get("content-type") ?? "application/octet-stream",
  };
}

export async function savePremiumInputFact(
  scenarioId: string,
  request: {
    readonly coordinate: PremiumCoordinate;
    readonly period: string;
    readonly input: PremiumProductionInput;
    readonly lobMix: readonly PremiumLobMix[];
    readonly stateMix?: readonly PremiumStateMix[];
    readonly note?: string;
    readonly changeSource?: "MANUAL_ENTRY" | "BULK_PASTE" | "RESEED";
  },
): Promise<SavePremiumInputFactResponse> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/premium-input-facts`,
    {
      method: "PUT",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(request),
    },
  );
  return (await parseJsonResponse(response)) as SavePremiumInputFactResponse;
}

export interface CopyPremiumYearForwardRequest {
  readonly coordinate: PremiumCoordinate;
  readonly sourceYear: number;
  readonly targetYears: readonly number[];
  readonly newBusinessGrowthRate?: number;
  readonly overwrite?: boolean;
}

export interface CopyPremiumYearForwardYearResult {
  readonly year: number;
  readonly out_of_horizon: boolean;
  readonly copied_periods: readonly string[];
  readonly skipped_existing_periods: readonly string[];
  readonly skipped_read_only_periods: readonly string[];
  readonly skipped_no_source_periods: readonly string[];
}

export interface CopyPremiumYearForwardResponse {
  readonly scenario_id: string;
  readonly coordinate: PremiumCoordinate;
  readonly source_year: number;
  readonly new_business_growth_rate: number;
  readonly overwrite: boolean;
  readonly copied_month_count: number;
  readonly years: readonly CopyPremiumYearForwardYearResult[];
  readonly affected_periods: readonly string[];
  readonly copied_at: string;
}

const CopyPremiumYearForwardResponseSchema = z.object({
  scenario_id: z.string(),
  coordinate: z.object({
    companyCode: z.string(),
    divisionCode: z.string(),
    productCode: z.string(),
  }),
  source_year: z.number().int(),
  new_business_growth_rate: z.number().finite(),
  overwrite: z.boolean(),
  copied_month_count: z.number().int().nonnegative(),
  years: z.array(
    z.object({
      year: z.number().int(),
      out_of_horizon: z.boolean(),
      copied_periods: z.array(z.string()),
      skipped_existing_periods: z.array(z.string()),
      skipped_read_only_periods: z.array(z.string()),
      skipped_no_source_periods: z.array(z.string()),
    }),
  ),
  affected_periods: z.array(z.string()),
  copied_at: z.string(),
});

/**
 * Populates the outer plan years of one product from the year before them -- UAT 2026-08-31.
 * Shawn's ask ("it would pull from the prior year... so on to 29") and the audit-premium caveat
 * that decides what "pull from the prior year" actually means are both in
 * `lib/premium-earning/copy-forward.ts` on the API side; this is only the wire call.
 */
export async function copyPremiumYearForward(
  scenarioId: string,
  request: CopyPremiumYearForwardRequest,
  signal?: AbortSignal,
): Promise<CopyPremiumYearForwardResponse> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/premium-input-facts/copy-forward`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(request),
      ...(signal === undefined ? {} : { signal }),
    },
  );
  return CopyPremiumYearForwardResponseSchema.parse(await parseJsonResponse(response));
}

export async function previewPremiumYear(
  scenarioId: string,
  request: {
    readonly coordinate: PremiumCoordinate;
    readonly months: readonly {
      readonly period: string;
      readonly input: PremiumProductionInput;
      readonly lobMix?: readonly PremiumLobMix[];
    }[];
  },
): Promise<PremiumYearPreviewResponse> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/premium-input-facts/preview`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(request),
    },
  );
  return (await parseJsonResponse(response)) as PremiumYearPreviewResponse;
}

export async function getFiarPriorYearBasis(
  scenarioId: string,
  input: {
    readonly companyCode: string;
    readonly divisionCode: string;
    readonly productCode: string;
    readonly sourceProduct: string;
    readonly targetYear: number;
    readonly actualsThroughPeriod: string;
    readonly planFallbackQuoteRates: readonly number[];
    readonly planFallbackHitRates: readonly number[];
  },
): Promise<FiarPriorYearBasisResponse> {
  const query = new URLSearchParams({
    companyCode: input.companyCode,
    divisionCode: input.divisionCode,
    productCode: input.productCode,
    sourceProduct: input.sourceProduct,
    targetYear: String(input.targetYear),
    actualsThroughPeriod: input.actualsThroughPeriod,
    planFallbackQuoteRates: input.planFallbackQuoteRates.join(","),
    planFallbackHitRates: input.planFallbackHitRates.join(","),
  });
  const url =
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}` +
    `/premium-input-facts/fiar-prior-year-basis?${query.toString()}`;
  // The governed FIAR LOB-mix query has taken ~130 seconds in UAT and the worker is intentionally
  // allowed five minutes. Keep polling through the worker's full execution window; the previous
  // two-minute client deadline reported a healthy in-flight job as a failure.
  const deadline = Date.now() + 6 * 60 * 1_000;
  let method: "POST" | "GET" = "POST";
  while (Date.now() < deadline) {
    const response = await fetch(url, { method, headers: authHeaders() });
    const payload = await parseJsonResponse(response);
    if (response.status === 200) return payload as FiarPriorYearBasisResponse;
    if (response.status !== 202 || !isFiarJobResponse(payload)) {
      throw new Error("Planning API returned an invalid FIAR background job response.");
    }
    method = "GET";
    await wait(Math.max(250, Math.min(payload.retry_after_ms, 5_000)));
  }
  throw new Error("FIAR prior-year basis is still loading. Try again in a moment.");
}

export async function getFiarSetupCatalog(
  scenarioId: string,
  options: FiarCatalogOptions = {},
): Promise<FiarSetupCatalog> {
  return FiarSetupCatalogSchema.parse(
    await pollFiarCatalog(async (signal, retry) => {
      const response = await fetch(
        `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/premium-input-facts/fiar-setup${retry ? "?retry=true" : ""}`,
        { headers: authHeaders(), signal },
      );
      return parseJsonResponse(response);
    }, options),
  );
}

export async function initializeFiarSetupProduct(
  scenarioId: string,
  input: {
    readonly coordinate: FiarSetupResult["coordinate"];
    readonly actualsThroughPeriod: string;
    readonly rates: FiarSetupRates;
    readonly poll: boolean;
    readonly mode?: "INITIALIZE" | "REPAIR_STATES";
  },
  signal?: AbortSignal,
): Promise<FiarSetupResult> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/premium-input-facts/fiar-setup`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(input),
      ...(signal === undefined ? {} : { signal }),
    },
  );
  return FiarSetupResultSchema.parse(await parseJsonResponse(response));
}

export interface PublishFiarOpeningCohortsResponse {
  readonly scenario_id: string;
  readonly job_id: string;
  readonly basis_year: number;
  readonly target_year: number;
  readonly published_month_count: number;
  readonly adjusted_month_count: number;
  readonly affected_periods: readonly string[];
  readonly published_at: string;
}

export async function publishFiarOpeningCohorts(
  scenarioId: string,
  request: {
    readonly jobId: string;
    readonly adjustments: readonly {
      readonly basisPeriod: string;
      readonly writtenPremiumAdjustment: number;
    }[];
    readonly adjustmentReason?: string;
  },
): Promise<PublishFiarOpeningCohortsResponse> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/premium-input-facts/fiar-prior-year-basis/publish`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(request),
    },
  );
  return (await parseJsonResponse(response)) as PublishFiarOpeningCohortsResponse;
}

export async function previewFiarPlanningSeed(
  scenarioId: string,
  batch: unknown,
): Promise<FiarPlanningSeedPreviewResponse> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/premium-input-facts/fiar-seed-preview`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(batch),
    },
  );
  return (await parseJsonResponse(response)) as FiarPlanningSeedPreviewResponse;
}

// --- Reinsurance projection and reporting (I07-12) ------------------------------------------

export interface TreatyTermPayload {
  readonly contractVersion: string;
  readonly treatyId: string;
  readonly treatyVersion: string;
  readonly mechanism: "AFFILIATE_FRONTING" | "QUOTA_SHARE" | "PROPERTY_XOL" | "CAT_XOL";
  readonly effectivePeriod: string;
  readonly expirationPeriod: string;
  readonly cedentEntityCode: string;
  readonly counterpartyEntityCode: string;
  readonly productCodes: readonly string[];
  readonly lobCodes: readonly string[];
  readonly cessionRate?: number;
  readonly cedingCommissionRate?: number;
  readonly premiumRate?: number;
  readonly placementPeriod?: string;
  readonly premiumBasis?: string;
  readonly lobPremiumBasis?: readonly {
    readonly lobCode: string;
    readonly premiumBasis: "GWP" | "GEP";
  }[];
  readonly finalYearConvention?: string;
  readonly affiliation: "AFFILIATE" | "NON_AFFILIATE";
  readonly direction: "CEDED";
  readonly eliminationFlag: boolean;
  readonly reinsuranceCode: "CEDED_AFFILIATE" | "CEDED_NON_AFFILIATE";
  readonly reinsuranceType: "QS" | "XOL" | "CAT" | "UNCATEGORIZED";
  readonly source: {
    readonly sourceSystem: string;
    readonly sourceDataset: string;
    readonly sourceVersion: string;
    readonly sourceAsOf: string;
  };
}

export interface TreatySnapshotPayload {
  readonly id: string;
  readonly contract_version: string;
  readonly source_system: string;
  readonly source_dataset: string;
  readonly source_version: string;
  readonly source_as_of: string;
  readonly schedule_checksum: string;
  readonly created_by_sub: string;
  readonly created_at: string;
  readonly terms: readonly TreatyTermPayload[];
}

export type TreatySnapshotSummaryPayload = Omit<TreatySnapshotPayload, "terms">;

export async function listTreatySnapshots(): Promise<{
  readonly snapshots: readonly TreatySnapshotSummaryPayload[];
}> {
  const response = await fetch(`${API_BASE}/api/planning/reinsurance/treaty-snapshots`, {
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as {
    readonly snapshots: readonly TreatySnapshotSummaryPayload[];
  };
}

export async function getTreatySnapshot(snapshotId: string): Promise<TreatySnapshotPayload> {
  const response = await fetch(
    `${API_BASE}/api/planning/reinsurance/treaty-snapshots/${encodeURIComponent(snapshotId)}`,
    { headers: authHeaders() },
  );
  const payload = (await parseJsonResponse(response)) as {
    readonly snapshot: TreatySnapshotPayload;
  };
  return payload.snapshot;
}

export type TreatySnapshotPreviewResponse =
  | {
      readonly valid: false;
      readonly issues: readonly {
        readonly path: string;
        readonly code: string;
        readonly message: string;
      }[];
    }
  | {
      readonly valid: true;
      readonly summary: {
        readonly contract_version: string;
        readonly formula_version: string;
        readonly source_system: string;
        readonly source_dataset: string;
        readonly source_version: string;
        readonly source_as_of: string;
        readonly schedule_checksum: string;
        readonly term_count: number;
        readonly treaty_count: number;
        readonly entity_codes: readonly string[];
        readonly effective_from: string;
        readonly effective_to: string;
        readonly mechanism_counts: Readonly<Record<TreatyTermPayload["mechanism"], number>>;
      };
      readonly source_version_status: "NEW" | "IDEMPOTENT" | "CONFLICT";
      readonly existing_snapshot_id: string | null;
      readonly publishable: boolean;
      readonly terms: readonly TreatyTermPayload[];
    };

export async function previewTreatySnapshot(
  terms: unknown,
): Promise<TreatySnapshotPreviewResponse> {
  const response = await fetch(`${API_BASE}/api/planning/reinsurance/treaty-snapshots/preview`, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ terms }),
  });
  return (await parseJsonResponse(response)) as TreatySnapshotPreviewResponse;
}

export async function publishTreatySnapshot(
  terms: unknown,
): Promise<{ readonly created: boolean; readonly snapshot: TreatySnapshotPayload }> {
  const response = await fetch(`${API_BASE}/api/planning/reinsurance/treaty-snapshots`, {
    method: "PUT",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ terms }),
  });
  return (await parseJsonResponse(response)) as {
    readonly created: boolean;
    readonly snapshot: TreatySnapshotPayload;
  };
}

export async function saveAffiliateCommissionOverride(
  scenarioId: string,
  request: {
    readonly coordinate: {
      readonly period: string;
      readonly entity_code: string;
      readonly division_code: string;
      readonly product_code: string;
      readonly lob_code: string;
      readonly accounting_basis: "STAT" | "GAAP";
    };
    readonly amount: number;
    readonly note?: string;
  },
): Promise<{ readonly saved: true }> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/reinsurance/affiliate-commission-overrides`,
    {
      method: "PUT",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(request),
    },
  );
  return (await parseJsonResponse(response)) as { readonly saved: true };
}

export interface AffiliateCommissionOverridePayload {
  readonly period: string;
  readonly entity_code: string;
  readonly division_code: string;
  readonly product_code: string;
  readonly lob_code: string;
  readonly state_code: string;
  readonly amount: number;
}

export interface ListAffiliateCommissionOverridesResponse {
  readonly scenario_id: string;
  readonly accounting_basis: "STAT" | "GAAP";
  readonly period_from: string;
  readonly period_to: string;
  readonly overrides: readonly AffiliateCommissionOverridePayload[];
}

/** Read side of the same coordinate the PUT above writes, so the entry grid round-trips the
 * amounts Finance actually saved rather than inferring them from calculated facts. */
export async function listAffiliateCommissionOverrides(
  scenarioId: string,
  accountingBasis: "STAT" | "GAAP" = "STAT",
): Promise<ListAffiliateCommissionOverridesResponse> {
  const query = new URLSearchParams({ accounting_basis: accountingBasis });
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/reinsurance/affiliate-commission-overrides?${query.toString()}`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as ListAffiliateCommissionOverridesResponse;
}

export interface RecomputeReinsuranceResponse {
  readonly scenario_id: string;
  readonly treaty_snapshot_id: string;
  readonly treaty_source_version: string;
  readonly formula_version: string;
  readonly accounting_basis: "STAT" | "GAAP";
  readonly period_from: string;
  readonly period_to: string;
  readonly fact_count: number;
}

export async function recomputeReinsurance(
  scenarioId: string,
  treatySnapshotId: string,
  accountingBasis: "STAT" | "GAAP",
): Promise<RecomputeReinsuranceResponse> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/reinsurance/recompute`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({
        treaty_snapshot_id: treatySnapshotId,
        accounting_basis: accountingBasis,
      }),
    },
  );
  return (await parseJsonResponse(response)) as RecomputeReinsuranceResponse;
}

export interface ReinsuranceReportFactPayload {
  readonly line:
    | "CEDED_WRITTEN_PREMIUM"
    | "CEDED_EARNED_PREMIUM"
    | "CEDING_COMMISSION"
    | "CEDED_LOSS";
  readonly amount: number;
  readonly period: string;
  readonly entity_code: string;
  readonly division_code: string;
  readonly product_code: string;
  readonly lob_code: string;
  readonly treaty_id: string;
  readonly treaty_version: string;
  readonly treaty_source_version: string;
  readonly affiliation: "AFFILIATE" | "NON_AFFILIATE";
  readonly direction: "CEDED" | "ASSUMED";
  readonly elimination_flag: boolean;
  readonly reinsurance_type: "QS" | "XOL" | "CAT" | "UNCATEGORIZED";
}

export interface ReinsuranceReportResponse {
  readonly scenario_id: string;
  readonly view: "STANDALONE" | "CONSOLIDATED";
  readonly formula_version: string;
  readonly facts: readonly ReinsuranceReportFactPayload[];
  readonly totals: readonly {
    readonly line: ReinsuranceReportFactPayload["line"];
    readonly amount: number;
  }[];
  readonly standalone_totals: readonly {
    readonly entity_code: string;
    readonly direction: "CEDED" | "ASSUMED";
    readonly line: ReinsuranceReportFactPayload["line"];
    readonly amount: number;
  }[];
  readonly elimination_checks: readonly {
    readonly period: string;
    readonly product_code: string;
    readonly lob_code: string;
    readonly treaty_id: string;
    readonly treaty_version: string;
    readonly line: ReinsuranceReportFactPayload["line"];
    readonly ceded_amount: number;
    readonly assumed_amount: number;
    readonly difference: 0;
  }[];
}

export async function getReinsuranceReport(
  scenarioId: string,
  view: "STANDALONE" | "CONSOLIDATED",
): Promise<ReinsuranceReportResponse> {
  const query = new URLSearchParams({ view, accounting_basis: "STAT" });
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/reinsurance/report?${query.toString()}`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as ReinsuranceReportResponse;
}

// --- Loss, LAE, reserving, and accident-year development (I07-13) ---------------------------

export interface LossAssumptionPayload {
  readonly assumptionId: string;
  readonly assumptionVersion: string;
  readonly effectivePeriod: string;
  readonly expirationPeriod: string;
  readonly entityCodes: readonly string[];
  readonly productCodes: readonly string[];
  readonly lobCodes: readonly string[];
  readonly lobCategory: string;
  readonly attritionalPick: {
    readonly annualRate: number;
    readonly quarterlyRates?: readonly number[];
  };
  readonly catPick?: { readonly annualRate: number; readonly quarterlyRates?: readonly number[] };
}

export interface LossAssumptionSnapshotPayload {
  readonly archived_at?: string | null;
  readonly id: string;
  readonly source_version: string;
  readonly source_as_of: string;
  readonly source_dataset: string;
  readonly schedule_checksum: string;
  readonly assumptions: readonly LossAssumptionPayload[];
}

export type LossAssumptionSnapshotSummaryPayload = Omit<
  LossAssumptionSnapshotPayload,
  "assumptions"
>;

export interface ReserveAssumptionSnapshotPayload {
  readonly id: string;
  readonly source_version: string;
  readonly source_as_of: string;
  readonly source_dataset: string;
  readonly schedule_checksum: string;
  readonly publication: {
    readonly contractVersion: "aire-planning-reserve-assumption/v1";
    readonly source: {
      readonly sourceSystem: string;
      readonly sourceDataset: string;
      readonly sourceVersion: string;
      readonly sourceAsOf: string;
    };
    readonly payoutCurves: readonly {
      readonly curveId: string;
      readonly curveVersion: string;
      readonly segment: "PROPERTY" | "CASUALTY_SHORT_TAIL" | "CASUALTY_LONG_TAIL";
      readonly effectiveAccidentYear: number;
      readonly expirationAccidentYear: number;
      readonly tailStatus: "FULLY_PAID" | "EXPLICIT_INCOMPLETE_TAIL";
      readonly developmentPoints: readonly {
        readonly developmentMonth: number;
        readonly cumulativePaidRate: number;
      }[];
    }[];
    readonly priorBookRunoffSchedules: readonly {
      readonly scheduleId: string;
      readonly scheduleVersion: string;
      readonly bookId: string;
      readonly entityCode: string;
      readonly grossCededNet: "GROSS" | "CEDED" | "NET";
      readonly openingValuationPeriod: string;
      readonly openingReserve: number;
      readonly remainingPoints: readonly {
        readonly valuationPeriod: string;
        readonly remainingRate: number;
      }[];
    }[];
    readonly adcRunoffSchedules: readonly {
      readonly adapterContractVersion: "aire-planning-adc/v1";
      readonly scheduleId: string;
      readonly scheduleVersion: string;
      readonly agreementId: string;
      readonly agreementVersion: string;
      readonly bookId: string;
      readonly assumedEntityCode: "MANGROVE";
      readonly cedentEntityCode: string;
      readonly affiliation: "AFFILIATE" | "NON_AFFILIATE";
      readonly accountingBasis: "STAT" | "GAAP";
      readonly openingValuationPeriod: string;
      readonly openingDiscountedReserve: number;
      readonly annualDiscountRate: number;
      readonly runoffPoints: readonly {
        readonly valuationPeriod: string;
        readonly remainingReserveRate: number;
        readonly discountAccretionRate: number;
      }[];
    }[];
    readonly deferredScheduleSlots: readonly {
      readonly slotType: string;
      readonly status: "AVAILABLE" | "DEFERRED";
      readonly adapterContractVersion?: string;
      readonly reason?: string;
    }[];
  };
}

export type ReserveAssumptionSnapshotSummaryPayload = Omit<
  ReserveAssumptionSnapshotPayload,
  "publication"
>;

export async function listLossAssumptionSnapshots(): Promise<{
  readonly snapshots: readonly LossAssumptionSnapshotSummaryPayload[];
}> {
  const response = await fetch(`${API_BASE}/api/planning/loss/assumption-snapshots`, {
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as {
    readonly snapshots: readonly LossAssumptionSnapshotSummaryPayload[];
  };
}

export async function getLossAssumptionSnapshot(
  snapshotId: string,
): Promise<LossAssumptionSnapshotPayload> {
  const response = await fetch(
    `${API_BASE}/api/planning/loss/assumption-snapshots/${encodeURIComponent(snapshotId)}`,
    { headers: authHeaders() },
  );
  const payload = (await parseJsonResponse(response)) as {
    readonly snapshot: LossAssumptionSnapshotPayload;
  };
  return payload.snapshot;
}

export async function setLossAssumptionArchived(
  snapshotId: string,
  archived: boolean,
): Promise<{ id: string; archived_at: string | null }> {
  const response = await fetch(
    `${API_BASE}/api/planning/loss/assumption-snapshots/${encodeURIComponent(snapshotId)}`,
    {
      method: "PATCH",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(z.object({ archived: z.boolean() }).strict().parse({ archived })),
    },
  );
  return z
    .object({
      id: z.string().uuid(),
      archived_at: z.string().datetime({ offset: true }).nullable(),
    })
    .parse(await parseJsonResponse(response));
}

export async function publishLossAssumptionSnapshot(
  assumptions: readonly unknown[],
): Promise<{ readonly created: boolean; readonly snapshot: LossAssumptionSnapshotPayload }> {
  const response = await fetch(`${API_BASE}/api/planning/loss/assumption-snapshots`, {
    method: "PUT",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ assumptions }),
  });
  return (await parseJsonResponse(response)) as {
    readonly created: boolean;
    readonly snapshot: LossAssumptionSnapshotPayload;
  };
}

export async function listReserveAssumptionSnapshots(): Promise<{
  readonly snapshots: readonly ReserveAssumptionSnapshotSummaryPayload[];
}> {
  const response = await fetch(`${API_BASE}/api/planning/loss/reserve-assumption-snapshots`, {
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as {
    readonly snapshots: readonly ReserveAssumptionSnapshotSummaryPayload[];
  };
}

export async function getReserveAssumptionSnapshot(
  snapshotId: string,
): Promise<ReserveAssumptionSnapshotPayload> {
  const response = await fetch(
    `${API_BASE}/api/planning/loss/reserve-assumption-snapshots/${encodeURIComponent(snapshotId)}`,
    { headers: authHeaders() },
  );
  const payload = (await parseJsonResponse(response)) as {
    readonly snapshot: ReserveAssumptionSnapshotPayload;
  };
  return payload.snapshot;
}

export async function publishReserveAssumptionSnapshot(
  publication: unknown,
): Promise<{ readonly created: boolean; readonly snapshot: ReserveAssumptionSnapshotPayload }> {
  const response = await fetch(`${API_BASE}/api/planning/loss/reserve-assumption-snapshots`, {
    method: "PUT",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ publication }),
  });
  return (await parseJsonResponse(response)) as {
    readonly created: boolean;
    readonly snapshot: ReserveAssumptionSnapshotPayload;
  };
}

export async function recomputeLossProjection(
  scenarioId: string,
  lossAssumptionSnapshotId: string,
  treatySnapshotId: string,
  accountingBasis: "STAT" | "GAAP",
): Promise<{
  readonly fact_count: number;
  readonly period_from: string;
  readonly period_to: string;
}> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/loss/recompute`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({
        loss_assumption_snapshot_id: lossAssumptionSnapshotId,
        treaty_snapshot_id: treatySnapshotId,
        accounting_basis: accountingBasis,
      }),
    },
  );
  return (await parseJsonResponse(response)) as {
    readonly fact_count: number;
    readonly period_from: string;
    readonly period_to: string;
  };
}

export async function recomputeReserveProjection(
  scenarioId: string,
  lossAssumptionSnapshotId: string,
  reserveAssumptionSnapshotId: string,
  accountingBasis: "STAT" | "GAAP",
): Promise<{
  readonly ultimate_count: number;
  readonly development_fact_count: number;
  readonly prior_book_fact_count: number;
  readonly adc_fact_count: number;
}> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/loss/reserve-recompute`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({
        loss_assumption_snapshot_id: lossAssumptionSnapshotId,
        reserve_assumption_snapshot_id: reserveAssumptionSnapshotId,
        accounting_basis: accountingBasis,
      }),
    },
  );
  return (await parseJsonResponse(response)) as {
    readonly ultimate_count: number;
    readonly development_fact_count: number;
    readonly prior_book_fact_count: number;
    readonly adc_fact_count: number;
  };
}

export type LossReportView = "LOSS" | "RESERVING" | "DEVELOPMENT";
export type LossReportPresentation = "STANDALONE" | "CONSOLIDATED";

export interface LossReserveReportFactPayload {
  readonly line: string;
  readonly amount: number;
  readonly period: string;
  readonly valuation_period: string;
  readonly accident_year: number;
  readonly reserve_segment: string;
  readonly entity_code: string;
  readonly division_code: string;
  readonly product_code: string;
  readonly lob_code: string;
  readonly state_code: string;
  readonly gross_ceded_net: "GROSS" | "CEDED" | "NET";
  readonly treaty_id: string;
  readonly treaty_version: string;
  readonly assumption_id: string;
  readonly assumption_version: string;
  readonly assumption_source_version: string;
  readonly ultimate_source_version: string;
  readonly payout_curve_id: string;
  readonly payout_curve_version: string;
}

export interface LossReserveReportResponse {
  readonly pagination?: {
    readonly page: number;
    readonly page_size: number;
    readonly total_rows: number;
    readonly total_pages: number;
  };
  readonly scenario_id: string;
  readonly view: LossReportView;
  readonly presentation: LossReportPresentation;
  readonly facts: readonly LossReserveReportFactPayload[];
  readonly written_year_summary?: readonly {
    readonly written_year: number;
    readonly entity_code: string;
    readonly division_code: string;
    readonly product_code: string;
    readonly lob_code: string;
    readonly reserve_segment: string;
    readonly attritional_amount: number;
    readonly cat_amount: number;
    readonly lae_amount: number;
    readonly total_amount: number;
  }[];
  readonly totals: readonly {
    readonly line: string;
    readonly amount: number;
    /** Present only for non-additive (balance / cumulative) lines. */
    readonly as_of_period?: string;
    /** Present only for non-additive lines, which are reported per basis. */
    readonly gross_ceded_net?: "GROSS" | "CEDED" | "NET";
  }[];
  readonly reserve_reconciliations: readonly {
    readonly valuation_period: string;
    readonly accident_year: number;
    readonly reserve_segment: string;
    readonly entity_code: string;
    readonly product_code: string;
    readonly lob_code: string;
    readonly gross_ceded_net: "GROSS" | "CEDED" | "NET";
    readonly treaty_id: string;
    readonly assumption_id: string;
    readonly paid_amount: number;
    readonly held_amount: number;
    readonly ultimate_amount: number;
    readonly difference: 0;
  }[];
  readonly adc_elimination_checks: readonly {
    readonly period: string;
    readonly line: string;
    readonly schedule_id: string;
    readonly schedule_version: string;
    readonly agreement_id: string;
    readonly agreement_version: string;
    readonly ceded_amount: number;
    readonly assumed_amount: number;
    readonly difference: 0;
  }[];
}

/**
 * Valuation window the report is scoped to. Both bounds are optional and are omitted from the
 * query string when absent, which leaves the server on the full scenario horizon. Because the
 * non-additive totals are the position at the latest valuation period *in the result set*,
 * narrowing this window restates those balances at the narrowed period rather than the horizon
 * end -- so it must be sent explicitly and never inferred.
 */
export interface LossReportValuationRange {
  readonly periodFrom?: string;
  readonly periodTo?: string;
  readonly page?: number;
  readonly pageSize?: number;
  readonly grossCededNet?: "GROSS" | "CEDED" | "NET";
}

export async function getLossReserveReport(
  scenarioId: string,
  view: LossReportView,
  presentation: LossReportPresentation = "STANDALONE",
  accountingBasis: "STAT" | "GAAP" = "STAT",
  valuationRange: LossReportValuationRange = {},
): Promise<LossReserveReportResponse> {
  const query = new URLSearchParams({ view, presentation, accounting_basis: accountingBasis });
  if (view === "LOSS") query.set("include_written_year_summary", "true");
  if (valuationRange.page !== undefined) query.set("page", String(valuationRange.page));
  if (valuationRange.pageSize !== undefined)
    query.set("page_size", String(valuationRange.pageSize));
  if (valuationRange.grossCededNet !== undefined)
    query.set("gross_ceded_net", valuationRange.grossCededNet);
  if (valuationRange.periodFrom !== undefined) {
    query.set("period_from", valuationRange.periodFrom);
  }
  if (valuationRange.periodTo !== undefined) {
    query.set("period_to", valuationRange.periodTo);
  }
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/loss/report?${query.toString()}`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as LossReserveReportResponse;
}

// --- Restricted workforce planning (I07-15) -----------------------------------------------

export type WorkforceComponent =
  | "SALARY"
  | "BONUS"
  | "PAYROLL_TAX"
  | "MEDICAL_RETIREMENT"
  | "HSA"
  | "ADMINISTRATION"
  | "COMMISSION"
  | "AUTO_ALLOWANCE"
  | "CAPITALIZED_LABOR_CREDIT"
  | "RECRUITING"
  | "SEVERANCE";

export interface WorkforceAggregateFactPayload {
  readonly period: string;
  readonly cost_center_code: string;
  readonly component: WorkforceComponent | "TOTAL_COMPENSATION";
  /** Fixed-precision server amount. The browser displays it and never derives a replacement. */
  readonly amount: string;
  readonly workforce_count: number;
  readonly formula_version: string;
  readonly computed_at: string;
}

export interface WorkforceAggregateReportResponse {
  readonly scenario_id: string;
  readonly minimum_reportable_cohort: number;
  readonly suppressed_fact_count: number;
  readonly facts: readonly WorkforceAggregateFactPayload[];
}

/**
 * One server-computed roll-up group. `amount` and `workforce_count` are both produced by the
 * workforce engine at this grain; the browser formats them and never adds, subtracts, or
 * re-derives them.
 */
export interface WorkforceSummaryRollupRowPayload {
  /** Period (`YYYY-MM`) or cost centre code, depending on the roll-up. */
  readonly key: string;
  readonly amount: string;
  readonly workforce_count: number;
}

export interface WorkforceSummaryRollupPayload {
  readonly rows: readonly WorkforceSummaryRollupRowPayload[];
  /** Groups the server withheld because it could not confirm a reportable cohort. */
  readonly suppressed_group_count: number;
  /** Groups found before suppression. Zero means the roll-up does not exist yet, not that it is 0. */
  readonly source_group_count: number;
}

export interface WorkforceSummaryReportResponse {
  readonly scenario_id: string;
  readonly minimum_reportable_cohort: number;
  readonly headcount_by_period: WorkforceSummaryRollupPayload;
  readonly salary_by_period: WorkforceSummaryRollupPayload;
  readonly total_compensation_by_cost_center: WorkforceSummaryRollupPayload;
  readonly annual_cost_center_review: {
    readonly rows: readonly {
      readonly fiscal_year: number;
      readonly cost_center_code: string;
      readonly ending_workforce_count: number;
      readonly salary_amount: string | null;
      readonly bonus_amount: string;
      readonly payroll_tax_amount: string;
      readonly benefits_amount: string;
      readonly other_compensation_amount: string;
      readonly non_salary_compensation_amount: string;
      readonly total_compensation_amount: string;
    }[];
    readonly suppressed_group_count: number;
    readonly source_group_count: number;
  };
}

export interface WorkforceEmployeeResultResponse {
  readonly scenario_id: string;
  readonly roster_snapshot_id: string;
  readonly workforce_ref: string;
  readonly profile: {
    readonly employee_name: string | null;
    readonly source_cost_center_code: string;
    readonly job_code: string;
    readonly job_title: string | null;
    readonly grade_code: string;
    readonly state_code: string;
    readonly region_code: string;
    readonly officer_class: "NONE" | "AVP" | "VP" | "SVP_EXECUTIVE";
    readonly start_date: string;
    readonly end_date: string | null;
    readonly fte: string;
    readonly is_new_hire: boolean;
    readonly event: {
      readonly type: "NONE" | "TRANSFER" | "PROMOTION";
      readonly effective_date: string | null;
      readonly destination_cost_center_code: string | null;
      readonly destination_job_code: string | null;
      readonly destination_job_title: string | null;
    };
  };
  readonly compensation_inputs: {
    readonly plan_salary_override: string | null;
    readonly merit_rate_overrides: Readonly<Record<string, string>>;
    readonly bonus_rate_override: string | null;
    readonly premium_target_rate: string;
    readonly commission_rate: string;
    readonly auto_allowance_annual: string;
    readonly capitalized_labor_rate: string;
  };
  readonly band_analysis: {
    readonly policy_snapshot_id: string;
    readonly job_code: string;
    readonly region_code: string;
    readonly minimum: string;
    readonly midpoint: string;
    readonly maximum: string;
    readonly salary_basis: string;
    readonly position_rate: string;
  } | null;
  readonly facts: readonly {
    readonly fiscal_year: number;
    readonly period: string;
    readonly cost_center_code: string;
    readonly component: WorkforceComponent;
    readonly amount: string;
    readonly formula_version: string;
    readonly computed_at: string;
  }[];
}

export interface WorkforceRosterReviewResponse {
  readonly scenario_id: string;
  readonly roster_snapshot_id: string;
  readonly rows: readonly {
    readonly fiscal_year: number;
    readonly workforce_ref: string;
    readonly employee_name: string | null;
    readonly cost_center_code: string;
    readonly job_code: string;
    readonly job_title: string | null;
    readonly grade_code: string;
    readonly region_code: string;
    readonly fte: string;
    readonly prior_year_salary: string | null;
    readonly prior_year_bonus_amount: string | null;
    readonly prior_year_total_compensation_amount: string | null;
    readonly year_over_year_total_compensation_difference: string | null;
    readonly annual_salary_amount: string;
    readonly merit_rate: string;
    readonly policy_merit_rate: string;
    readonly salary_structure_rate: string;
    readonly bonus_amount: string;
    readonly bonus_target_minimum_rate: string | null;
    readonly bonus_target_midpoint_rate: string | null;
    readonly bonus_target_maximum_rate: string | null;
    readonly commission_amount: string;
    readonly state_code: string;
    readonly taxable_wages_amount: string;
    readonly social_security_taxable_wages: string;
    readonly social_security_tax_amount: string;
    readonly social_security_rate: string;
    readonly social_security_wage_base: string;
    readonly social_security_capped: boolean;
    readonly medicare_tax_amount: string;
    readonly medicare_rate: string;
    readonly futa_taxable_wages: string;
    readonly futa_tax_amount: string;
    readonly futa_rate: string;
    readonly futa_wage_base: string;
    readonly futa_capped: boolean;
    readonly suta_taxable_wages: string;
    readonly suta_tax_amount: string;
    readonly suta_rate: string;
    readonly suta_wage_base: string;
    readonly suta_capped: boolean;
    readonly raise_effective_month: number;
    readonly payroll_tax_amount: string;
    readonly benefits_amount: string;
    readonly other_compensation_amount: string;
    readonly total_compensation_amount: string;
    readonly band_position_rate: string | null;
    readonly band_minimum: string | null;
    readonly band_midpoint: string | null;
    readonly band_maximum: string | null;
    readonly band_salary_basis: string | null;
    readonly compa_ratio: string | null;
    readonly range_penetration_rate: string | null;
    readonly band_zone:
      | "BELOW_P25"
      | "FIRST_QUARTER"
      | "SECOND_QUARTER"
      | "THIRD_QUARTER"
      | "FOURTH_QUARTER"
      | "ABOVE_P75"
      | null;
    readonly status: "ACTIVE" | "NEW_HIRE" | "PLANNED_TERM" | "TRANSFER" | "PROMOTION";
  }[];
  readonly headcount_rollforward_rows: readonly {
    readonly fiscal_year: number;
    readonly beginning_headcount: number;
    readonly planned_hire_count: number;
    readonly planned_termination_count: number;
    readonly ending_headcount: number;
  }[];
  readonly compensation_rollforward_rows: readonly {
    readonly fiscal_year: number;
    readonly prior_year_total_compensation_amount: string;
    readonly continuing_salary_change_amount: string;
    readonly planned_hire_change_amount: string;
    readonly planned_termination_change_amount: string;
    readonly other_compensation_change_amount: string;
    readonly total_compensation_amount: string;
  }[];
  readonly tax_summary_rows: readonly {
    readonly fiscal_year: number;
    readonly cost_center_code: string;
    readonly status: "ACTIVE" | "NEW_HIRE" | "PLANNED_TERM" | "TRANSFER" | "PROMOTION" | null;
    readonly workforce_count: number;
    readonly taxable_wages_amount: string;
    readonly social_security_tax_amount: string;
    readonly medicare_tax_amount: string;
    readonly futa_tax_amount: string;
    readonly suta_tax_amount: string;
    readonly payroll_tax_amount: string;
    readonly total_compensation_amount: string;
  }[];
  readonly review_summary_rows: readonly {
    readonly fiscal_year: number;
    readonly cost_center_code: string;
    readonly status: "ACTIVE" | "NEW_HIRE" | "PLANNED_TERM" | "TRANSFER" | "PROMOTION" | null;
    readonly workforce_count: number;
    readonly fte: string;
    readonly prior_year_salary_amount: string;
    readonly prior_year_bonus_amount: string;
    readonly salary_amount: string;
    readonly bonus_amount: string;
    readonly commission_amount: string;
    readonly payroll_tax_amount: string;
    readonly benefits_amount: string;
    readonly other_compensation_amount: string;
    readonly total_compensation_amount: string;
    readonly new_hire_count: number;
    readonly planned_term_count: number;
    readonly merit_override_count: number;
    readonly global_merit_amount: string;
    readonly override_merit_amount: string;
    readonly total_merit_amount: string;
    readonly banded_count: number;
    readonly average_compa_ratio: string | null;
    readonly average_range_penetration_rate: string | null;
    readonly below_band_count: number;
    readonly in_band_count: number;
    readonly above_band_count: number;
    readonly band_zone_counts: Readonly<Record<string, number>>;
  }[];
  readonly dimension_summary_rows: readonly {
    readonly fiscal_year: number;
    readonly cost_center_code: string;
    readonly status: "ACTIVE" | "NEW_HIRE" | "PLANNED_TERM" | "TRANSFER" | "PROMOTION" | null;
    readonly dimension: "GRADE" | "REGION";
    readonly dimension_key: string;
    readonly workforce_count: number;
    readonly fte: string;
    readonly total_compensation_amount: string;
    readonly average_compa_ratio: string | null;
    readonly average_range_penetration_rate: string | null;
    readonly out_of_band_count: number;
  }[];
}

export interface WorkforcePolicySnapshotResponse {
  readonly snapshot_id: string;
  readonly policy: {
    readonly contractVersion: "aire-planning-workforce-policy/v1";
    readonly formulaVersion: "finance-planning-workforce-v1";
    readonly sourceSystem: string;
    readonly sourceVersion: string;
    readonly effectiveFrom: string;
    readonly effectiveTo: string | null;
    readonly meritRate: string;
    readonly salaryStructureRate: string;
    readonly raiseEffectiveMonth: number;
    readonly payrollTax: {
      readonly socialSecurityRate: string;
      readonly socialSecurityWageBase: string;
      readonly medicareRate: string;
      readonly futaRate: string;
      readonly futaWageBase: string;
    };
    readonly benefits: {
      readonly medicalMonthlyPremium: string;
      readonly medicalParticipationRate: string;
      readonly retirementMatchRate: string;
      readonly retirementParticipationRate: string;
      readonly retirementAnnualCap: string;
      readonly hsaAnnualAmount: string;
      readonly hsaParticipationRate: string;
      readonly administrationMonthlyAmount: string;
    };
    readonly recruiterFeeRate: string;
    readonly bonusHaircutRate: string;
    readonly severance: {
      readonly underThreeYearsWeeks: string;
      readonly threeYearsWeeks: string;
      readonly fourYearsWeeks: string;
      readonly fivePlusYearsWeeks: string;
      readonly avpAdditionalWeeks: string;
      readonly vpAdditionalWeeks: string;
      readonly svpExecutiveAdditionalWeeks: string;
    };
    readonly stateUnemployment: readonly {
      readonly stateCode: string;
      readonly rate: string;
      readonly wageBase: string;
    }[];
    readonly salaryBands: readonly {
      readonly jobCode: string;
      readonly regionCode: string;
      readonly minimum: string;
      readonly midpoint: string;
      readonly maximum: string;
    }[];
    readonly bonusPolicies: readonly {
      readonly gradeCode: string;
      readonly minimumRate: string;
      readonly midpointRate: string;
      readonly maximumRate: string;
    }[];
  };
}

export interface RecomputeWorkforceResponse {
  readonly scenario_id: string;
  readonly policy_snapshot_id: string;
  readonly roster_snapshot_id: string;
  readonly fiscal_years: readonly number[];
  readonly monthly_fact_count: number;
  readonly aggregate_fact_count: number;
  readonly control_count: number;
  readonly op_ex_bridge_fact_count: number;
  readonly formula_version: string;
  readonly computed_at: string;
}

export interface WorkforcePolicySnapshotSummaryPayload {
  readonly snapshot_id: string;
  readonly source_system: string;
  readonly source_version: string;
  readonly effective_from: string;
  readonly effective_to: string | null;
}

export interface WorkforceRosterSnapshotSummaryPayload {
  readonly snapshot_id: string;
  readonly scenario_id: string;
  readonly source_kind: "SYNTHETIC" | "AUTHORITATIVE_HR";
  readonly source_system: string;
  readonly source_version: string;
  readonly source_as_of: string;
  readonly sample_workforce_ref: string | null;
}

export interface DevWorkforceBaselineResponse {
  readonly scenario_id: string;
  readonly policy_snapshot_id: string;
  readonly roster_snapshot_id: string;
  readonly policy_created: boolean;
  readonly roster_created: boolean;
  readonly record_count: number;
  readonly label: "SYNTHETIC DEV DATA — NOT FINANCE/HR APPROVED";
}

export type WorkforceRosterRevisionChangePayload =
  | {
      readonly type: "TERMINATION";
      readonly workforce_ref: string;
      readonly effective_date: string;
    }
  | {
      readonly type: "TRANSFER";
      readonly workforce_ref: string;
      readonly effective_date: string;
      readonly destination_cost_center_code: string;
      readonly destination_job_code?: string;
    }
  | {
      readonly type: "PROMOTION";
      readonly workforce_ref: string;
      readonly effective_date: string;
      readonly destination_job_title: string;
      readonly salary_override?: string;
    }
  | {
      readonly type: "REMOVE_PLANNED_CHANGE";
      readonly workforce_ref: string;
    }
  | {
      readonly type: "COMPENSATION_OVERRIDE";
      readonly workforce_ref: string;
      readonly fiscal_year: number;
      readonly plan_salary_override: string | null;
      readonly merit_rate_override: string | null;
      readonly bonus_rate_override: string | null;
      readonly premium_target_rate: string;
      readonly commission_rate: string;
      readonly auto_allowance_annual: string;
      readonly capitalized_labor_rate: string;
    }
  | {
      readonly type: "MERIT_OVERRIDE";
      readonly workforce_ref: string;
      readonly fiscal_year: number;
      readonly merit_rate_override: string | null;
    }
  | {
      readonly type: "HIRE";
      readonly employee_set_ref: string;
      readonly source_cost_center_code: string;
      readonly job_code: string;
      readonly job_title: string;
      readonly grade_code: string;
      readonly state_code: string;
      readonly region_code: string;
      readonly officer_class: "NONE" | "AVP" | "VP" | "SVP_EXECUTIVE";
      readonly start_date: string;
      readonly fte: string;
      readonly plan_salary_override?: string;
      readonly bonus_rate_override?: string;
      readonly premium_target_rate?: string;
      readonly commission_rate?: string;
      readonly auto_allowance_annual?: string;
      readonly capitalized_labor_rate?: string;
      readonly recruiter_fee_applies: boolean;
    };

export interface WorkforceRosterRevisionResponse {
  readonly scenario_id: string;
  readonly parent_roster_snapshot_id: string;
  readonly roster_snapshot_id: string;
  readonly workforce_ref: string;
  readonly change_type:
    | "HIRE"
    | "TERMINATION"
    | "TRANSFER"
    | "PROMOTION"
    | "REMOVE_PLANNED_CHANGE"
    | "COMPENSATION_OVERRIDE"
    | "MERIT_OVERRIDE";
  readonly checksum: string;
  readonly created: boolean;
}

export async function listWorkforcePolicySnapshots(): Promise<{
  readonly snapshots: readonly WorkforcePolicySnapshotSummaryPayload[];
}> {
  const response = await fetch(`${API_BASE}/api/planning/workforce/policy-snapshots`, {
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as {
    readonly snapshots: readonly WorkforcePolicySnapshotSummaryPayload[];
  };
}

export async function listWorkforceRosterSnapshots(scenarioId: string): Promise<{
  readonly snapshots: readonly WorkforceRosterSnapshotSummaryPayload[];
}> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/workforce/roster-snapshots`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as {
    readonly snapshots: readonly WorkforceRosterSnapshotSummaryPayload[];
  };
}

export async function createDevWorkforceBaseline(
  scenarioId: string,
): Promise<DevWorkforceBaselineResponse> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/workforce/dev-baseline`,
    { method: "POST", headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as DevWorkforceBaselineResponse;
}

export async function publishWorkforcePolicy(
  request: unknown,
): Promise<{ readonly snapshot_id: string; readonly checksum: string; readonly created: boolean }> {
  const response = await fetch(`${API_BASE}/api/planning/workforce/policy-snapshots`, {
    method: "PUT",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify(request),
  });
  return (await parseJsonResponse(response)) as {
    readonly snapshot_id: string;
    readonly checksum: string;
    readonly created: boolean;
  };
}

export async function publishWorkforceRoster(
  scenarioId: string,
  request: unknown,
): Promise<{ readonly snapshot_id: string; readonly checksum: string; readonly created: boolean }> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/workforce/roster-snapshots`,
    {
      method: "PUT",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(request),
    },
  );
  return (await parseJsonResponse(response)) as {
    readonly snapshot_id: string;
    readonly checksum: string;
    readonly created: boolean;
  };
}

export async function createWorkforceRosterRevision(
  scenarioId: string,
  parentRosterSnapshotId: string,
  revisionId: string,
  change: WorkforceRosterRevisionChangePayload,
): Promise<WorkforceRosterRevisionResponse> {
  const path =
    API_BASE +
    "/api/planning/scenarios/" +
    encodeURIComponent(scenarioId) +
    "/workforce/roster-snapshots/" +
    encodeURIComponent(parentRosterSnapshotId) +
    "/revisions";
  const response = await fetch(path, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ revision_id: revisionId, change }),
  });
  return (await parseJsonResponse(response)) as WorkforceRosterRevisionResponse;
}

export async function getWorkforceAggregateReport(
  scenarioId: string,
): Promise<WorkforceAggregateReportResponse> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/workforce/aggregate-report`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as WorkforceAggregateReportResponse;
}

/**
 * Cohort-safe workforce roll-ups. This is a separate server report rather than a client-side
 * reduction over `getWorkforceAggregateReport`: that response has already had below-threshold
 * cost-centre rows removed, so summing it would present a partial as a total.
 */
export async function getWorkforceSummaryReport(
  scenarioId: string,
): Promise<WorkforceSummaryReportResponse> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/workforce/summary-report`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as WorkforceSummaryReportResponse;
}

export async function getWorkforcePolicySnapshot(
  snapshotId: string,
): Promise<WorkforcePolicySnapshotResponse> {
  const response = await fetch(
    `${API_BASE}/api/planning/workforce/policy-snapshots/${encodeURIComponent(snapshotId)}`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as WorkforcePolicySnapshotResponse;
}

export async function getWorkforceEmployeeResult(
  scenarioId: string,
  rosterSnapshotId: string,
  workforceRef: string,
): Promise<WorkforceEmployeeResultResponse> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}` +
      `/workforce/roster-snapshots/${encodeURIComponent(rosterSnapshotId)}` +
      `/records/${encodeURIComponent(workforceRef)}/results`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as WorkforceEmployeeResultResponse;
}

export async function getWorkforceRosterReview(
  scenarioId: string,
  rosterSnapshotId: string,
): Promise<WorkforceRosterReviewResponse> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}` +
      `/workforce/roster-snapshots/${encodeURIComponent(rosterSnapshotId)}/review`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as WorkforceRosterReviewResponse;
}

export async function recomputeWorkforce(
  scenarioId: string,
  policySnapshotId: string,
  rosterSnapshotId: string,
): Promise<RecomputeWorkforceResponse> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/workforce/recompute`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({
        policy_snapshot_id: policySnapshotId,
        roster_snapshot_id: rosterSnapshotId,
      }),
    },
  );
  return (await parseJsonResponse(response)) as RecomputeWorkforceResponse;
}

export interface FinancialDriverSnapshotSummary {
  readonly id: string;
  readonly contract_version: string;
  readonly formula_version: string;
  readonly source_system: string;
  readonly source_dataset: string;
  readonly source_version: string;
  readonly source_as_of: string;
  readonly snapshot_checksum: string;
  readonly created_by_sub: string;
  readonly created_at: string;
  readonly entity_codes: readonly string[];
  readonly period_from: string;
  readonly period_to: string;
}

export interface FinancialDriverSnapshotResponse extends FinancialDriverSnapshotSummary {
  readonly publication: {
    readonly contractVersion: string;
    readonly formulaVersion: string;
    readonly source: {
      readonly sourceSystem: string;
      readonly sourceDataset: string;
      readonly sourceVersion: string;
      readonly sourceAsOf: string;
    };
    readonly entities: readonly FinancialDriverEntity[];
  };
}

export interface FinancialDacDetail {
  readonly divisionCode: string;
  readonly productCode: string;
  readonly lobCode: string;
  readonly openingGrossDac: string;
  readonly openingCededDac: string;
  readonly sourceFactIds: readonly string[];
  readonly periods: readonly {
    readonly period: string;
    readonly grossDacDeferred: string;
    readonly grossDacAmortized: string;
    readonly cededDacDeferred: string;
    readonly cededDacAmortized: string;
    readonly sourceFactIds: readonly string[];
  }[];
}

export interface FinancialDriverEntity {
  readonly dacDetails?: readonly FinancialDacDetail[];
  readonly entityCode: string;
  readonly dayCountBasis: 360 | 365 | 366;
  readonly openingGrossDac: string;
  readonly openingCededDac: string;
  readonly openingBalances: readonly {
    readonly basis: "STAT" | "GAAP";
    readonly position: Readonly<Record<string, string>>;
    readonly sourceFactIds: readonly string[];
  }[];
  readonly openingInvestmentClasses: readonly {
    readonly assetClass: string;
    readonly openingBookValue: string;
    readonly openingAnnualBookYield: string;
    readonly taxTreatment: string;
  }[];
  readonly periods: readonly {
    readonly period: string;
    readonly otherIncome: string;
    readonly grossDacDeferred: string;
    readonly grossDacAmortized: string;
    readonly cededDacDeferred: string;
    readonly cededDacAmortized: string;
    readonly depreciation: string;
    readonly capitalExpenditures: string;
    readonly endingNetDeferredTaxAsset: string;
    readonly investmentClasses: readonly {
      readonly assetClass: string;
      readonly maturity: string;
      readonly netNewInvestment: string;
      readonly newMoneyAnnualYield: string;
    }[];
    readonly taxPolicy: {
      readonly effectiveRate: string;
      readonly municipalIncomeExempt: boolean;
      readonly recognizeLossBenefit: boolean;
    };
  }[];
}

export async function getFinancialDriverSnapshots(scenarioId?: string): Promise<{
  readonly snapshots: readonly FinancialDriverSnapshotSummary[];
}> {
  const query =
    scenarioId === undefined
      ? ""
      : `?${new URLSearchParams({ scenario_id: scenarioId }).toString()}`;
  const response = await fetch(`${API_BASE}/api/planning/financial-driver-snapshots${query}`, {
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as {
    readonly snapshots: readonly FinancialDriverSnapshotSummary[];
  };
}

export async function getFinancialDriverSnapshot(
  snapshotId: string,
): Promise<FinancialDriverSnapshotResponse> {
  const response = await fetch(
    `${API_BASE}/api/planning/financial-driver-snapshots/${encodeURIComponent(snapshotId)}`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as FinancialDriverSnapshotResponse;
}

export type FinancialDriverPreviewResponse =
  | {
      readonly valid: false;
      readonly issues: readonly {
        readonly path: string;
        readonly code: string;
        readonly message: string;
      }[];
    }
  | {
      readonly valid: true;
      readonly summary: {
        readonly contract_version: string;
        readonly formula_version: string;
        readonly source_system: string;
        readonly source_dataset: string;
        readonly source_version: string;
        readonly source_as_of: string;
        readonly snapshot_checksum: string;
        readonly entity_codes: readonly string[];
        readonly period_from: string;
        readonly period_to: string;
        readonly entity_count: number;
        readonly period_count: number;
        readonly investment_class_row_count: number;
        readonly source_fact_count: number;
      };
      readonly source_version_status: "NEW" | "IDEMPOTENT" | "CONFLICT";
      readonly existing_snapshot_id: string | null;
      readonly scenario_eligibility: {
        readonly scenario_id: string;
        readonly scenario_company_codes: readonly string[];
        readonly expected_period_from: string;
        readonly expected_period_to: string;
        readonly eligible: boolean;
        readonly checks: readonly {
          readonly code: "COMPLETE_HORIZON" | "ENTITY_COVERAGE" | "ACTUALS_WINDOW";
          readonly passed: boolean;
          readonly message: string;
        }[];
      };
      readonly publishable: boolean;
    };

export async function previewFinancialDriverSnapshot(
  scenarioId: string,
  publication: unknown,
): Promise<FinancialDriverPreviewResponse> {
  const response = await fetch(`${API_BASE}/api/planning/financial-driver-snapshots/preview`, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ scenario_id: scenarioId, publication }),
  });
  return (await parseJsonResponse(response)) as FinancialDriverPreviewResponse;
}

export async function publishFinancialDriverSnapshot(
  scenarioId: string,
  publication: unknown,
): Promise<{ readonly created: boolean; readonly snapshot: FinancialDriverSnapshotResponse }> {
  const response = await fetch(`${API_BASE}/api/planning/financial-driver-snapshots`, {
    method: "PUT",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ scenario_id: scenarioId, publication }),
  });
  return (await parseJsonResponse(response)) as {
    readonly created: boolean;
    readonly snapshot: FinancialDriverSnapshotResponse;
  };
}

export interface FinancialStatementFactPayload {
  readonly division_code?: string;
  readonly product_code?: string;
  readonly lob_code?: string;
  readonly source_fact_ids?: readonly string[];
  readonly period: string;
  readonly entity_code: string;
  readonly accounting_basis: "STAT" | "GAAP";
  readonly line: string;
  readonly amount: string;
  readonly formula_version: string;
}

export interface FinancialStatementControlPayload {
  readonly period: string;
  readonly entity_code: string;
  readonly accounting_basis: "STAT" | "GAAP";
  readonly control_type: string;
  readonly difference: string;
}

export interface FinancialInvestmentClassResultPayload {
  readonly period: string;
  readonly entity_code: string;
  readonly asset_class: string;
  readonly beginning_book_value: string;
  readonly beginning_annual_book_yield: string;
  readonly maturity: string;
  readonly net_new_investment: string;
  readonly reinvested_principal: string;
  readonly new_money_annual_yield: string;
  readonly ending_book_value: string;
  readonly ending_annual_book_yield: string;
  readonly net_investment_income: string;
  readonly tax_exempt_municipal_income: string;
  readonly source_fact_ids: readonly string[];
  readonly formula_version: string;
}

/**
 * One LIC period of the liquidity policy overlay (PG-17, ADR-059).
 *
 * Every field name is deliberately unable to read as a balance — `indicative*`, `*_floor`,
 * `*_ratio`, and no `cash` or `invested_assets`. The server carries `basis: "POLICY_OVERLAY"` and
 * `is_authoritative: false` as literal discriminants for the same reason. Mirror them here rather
 * than widening them: an overlay row that could be mistaken for a statement row is the failure mode
 * the whole design guards against.
 */
export interface LiquidityOverlayRowPayload {
  readonly period: string;
  readonly entity_code: string;
  readonly basis: "POLICY_OVERLAY";
  readonly statement_basis: "STAT" | "GAAP";
  readonly is_authoritative: false;
  readonly method:
    | "FLAT"
    | "DAYS_OF_NET_LOSS_AND_LAE"
    | "PERCENT_OF_NET_LOSS_AND_LAE_RESERVES"
    | "PERCENT_OF_NET_WRITTEN_PREMIUM";
  readonly minimum_cash_floor: string;
  readonly pre_available_cash: string;
  readonly surplus_over_floor: string;
  readonly shortfall_to_floor: string;
  readonly indicative_sweep: string;
  readonly indicative_draw: string;
  readonly coverage_ratio: string;
  readonly status: "COMPLIANT" | "BELOW_FLOOR" | "UNFUNDABLE";
  readonly formula_version: string;
}

export interface LiquidityOverlayControlPayload {
  readonly period: string;
  readonly entity_code: string;
  readonly basis: "POLICY_OVERLAY";
  readonly control_type: "LIQUIDITY_OVERLAY_TIE_OUT";
  readonly difference: string;
}

export interface LiquidityOverlayPayload {
  readonly is_authoritative: false;
  readonly formula_version: string;
  readonly rows: readonly LiquidityOverlayRowPayload[];
  readonly controls: readonly LiquidityOverlayControlPayload[];
}

export interface FinancialStatementReportResponse {
  readonly scenario_id: string;
  readonly financial_driver_snapshot_id: string | null;
  readonly source_version: string | null;
  readonly computed_at: string | null;
  readonly facts: readonly FinancialStatementFactPayload[];
  readonly controls: readonly FinancialStatementControlPayload[];
  readonly investment_classes: readonly FinancialInvestmentClassResultPayload[];
  /**
   * Absent — not null, not an empty object — when no liquidity policy is published. Optional here to
   * match the server, so a scenario without a policy renders exactly as before.
   */
  readonly liquidity_overlay?: LiquidityOverlayPayload;
}

export async function getFinancialStatementReport(
  scenarioId: string,
  entityCode?: string,
  grain: "MONTH" | "QUARTER" | "YEAR" = "MONTH",
): Promise<FinancialStatementReportResponse> {
  const query = `?${new URLSearchParams({
    ...(entityCode === undefined ? {} : { entity_code: entityCode }),
    grain,
  }).toString()}`;
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/financial-statements/report${query}`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as FinancialStatementReportResponse;
}

export interface ExecutiveSummaryMetricPayload {
  readonly code: string;
  readonly category: "PERFORMANCE" | "POSITION" | "CASH_FLOW" | "RATIO";
  readonly label: string;
  readonly format: "MONEY" | "PERCENT";
  readonly value: string | null;
  readonly source_fact_ids: readonly string[];
}

export interface ExecutiveSummaryResponse {
  readonly scenario_id: string;
  readonly financial_driver_snapshot_id: string;
  readonly source_version: string;
  readonly computed_at: string;
  readonly basis: "STAT" | "GAAP";
  readonly grain: "MONTH" | "QUARTER" | "YEAR";
  readonly formula_version: string;
  readonly controls_passed: boolean;
  readonly control_count: number;
  readonly periods: readonly {
    readonly period: string;
    readonly metrics: readonly ExecutiveSummaryMetricPayload[];
  }[];
  readonly readiness: {
    readonly actuals_comparison: {
      readonly status: "UNAVAILABLE";
      readonly reason: "GOVERNED_ACTUALS_CONTRACT_NOT_CONNECTED";
    };
    readonly board_output: {
      readonly status: "UNAVAILABLE";
      readonly reason: "REPORT_CONSUMER_CONTRACT_NOT_CONNECTED";
    };
  };
}

export async function getExecutiveSummary(
  scenarioId: string,
  basis: "STAT" | "GAAP" = "GAAP",
  grain: "MONTH" | "QUARTER" | "YEAR" = "YEAR",
  periodFrom?: string,
  periodTo?: string,
): Promise<ExecutiveSummaryResponse> {
  const query = new URLSearchParams({
    basis,
    grain,
    ...(periodFrom === undefined || periodFrom === "" ? {} : { period_from: periodFrom }),
    ...(periodTo === undefined || periodTo === "" ? {} : { period_to: periodTo }),
  }).toString();
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/financial-statements/executive-summary?${query}`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as ExecutiveSummaryResponse;
}

// --- Generic cube query (I07-18, POST /api/planning/cube/query) ------------------------------

/**
 * The generic fact-query surface (ADR-056). `PlanComputedFact` is one wide fact table, so nearly
 * every planning report is a pivot of it; this client plus `FactPivot` are what turn a report into
 * a configuration object rather than a bespoke component.
 *
 * TYPES ARE DECLARED LOCALLY, not imported. `apps/shell/web` has no dependency on any planning-api
 * package, so every lane in this file hand-declares its `readonly` snake_case payload interfaces
 * (see `FinancialStatementReportResponse` above). These mirror
 * `apps/planning/api/src/schemas/cube-query.ts` field-for-field.
 *
 * THE REQUEST BODY IS ENTIRELY snake_case AND `.strict()`. A single camelCase key is a 400 with
 * `unrecognized_keys`, not a silently dropped field. The only camelCase in a request is a dimension
 * *value* string inside `grouping`/`filters` (`"entityCode"`, `"grossCededNet"`, ...), because those
 * are Prisma field names.
 *
 * EVERY RATIO MEASURE REQUIRES `grossCededNet` IN `grouping` (HAZARD 1 in
 * `apps/planning/api/src/lib/cube/query-contract.ts`). That guard tests `!summableAcrossRows` with
 * no ratio exemption, and every ratio measure is `summableAcrossRows: false`, so omitting the axis
 * is `AIRE_PLANNING_CUBE_INCOHERENT_MEASURE_SET` / 400. For statement facts the axis is harmless --
 * the writer persists `grossCededNet: "NA"` for all statement lines, so the forced axis collapses to
 * a single degenerate member.
 */
export type CubeGroupableDimension =
  | "period"
  | "entityCode"
  | "accountingBasis"
  | "divisionCode"
  | "productCode"
  | "lobCode"
  | "stateCode"
  | "grossCededNet"
  | "reinsuranceDirection"
  | "accidentYear"
  | "reserveSegment"
  | "valuationPeriod"
  | "treatyId";

/**
 * The frozen FROM clause (ADR-056 Decision 1). `SCENARIO` reads `plan_computed_fact`,
 * `LOCKED_VERSION` reads `plan_locked_computed_fact`; no field names a table.
 */
export type CubeQuerySourcePayload =
  | { readonly type: "SCENARIO"; readonly scenario_id: string }
  | { readonly type: "LOCKED_VERSION"; readonly locked_version_id: string };

export interface CubeQueryRequestPayload {
  readonly source: CubeQuerySourcePayload;
  /** 1..12 registered measure codes. An unregistered code is a validation failure, not an empty column. */
  readonly measures: readonly string[];
  /** 0..4 groupable dimensions. Ratio measures require `grossCededNet` here. */
  readonly grouping: readonly CubeGroupableDimension[];
  /** Both bounds required: an absent bound cannot be allowed to mean "all history". */
  readonly period_from: string;
  readonly period_to: string;
  readonly filters: Readonly<Partial<Record<CubeGroupableDimension, readonly string[]>>>;
  readonly entity_code?: string;
  /** Required with no default -- STAT and GAAP coexist per coordinate. */
  readonly accounting_basis: "STAT" | "GAAP";
}

export interface CubeQueryRowPayload {
  readonly coordinate: Readonly<Partial<Record<CubeGroupableDimension, string>>>;
  /**
   * Measure code -> value, serialized as a decimal string to preserve stored precision. Ratios are
   * rendered at 8 decimal places.
   *
   * `null` is legal and means exactly one thing: a recomputed ratio whose denominator aggregated to
   * zero. Such a ratio is **undefined**, not zero -- rendering `"0"` would show a real-looking 0%
   * loss ratio for a group with no earned premium. Amount and count measures are never `null`.
   */
  readonly values: Readonly<Record<string, string | null>>;
}

/**
 * Per-measure provenance echoed alongside the numbers. `summable_across_rows` and
 * `time_aggregation` are what tell a consumer whether a column may be totalled; a client that
 * renders a total without consulting them is the defect ADR-056 cites.
 */
export interface CubeMeasureMetadataPayload {
  readonly time_aggregation: "FIRST" | "LAST" | "SUM" | "RECOMPUTE_FROM_COMPONENTS";
  readonly sign_convention: "POSITIVE_MAGNITUDE" | "AS_STORED";
  readonly planning_convention: {
    readonly mode: "REQUIRE" | "EXCLUDE";
    readonly value: string;
  };
  readonly formula_version: string;
  readonly summable_across_rows: boolean;
  readonly format: "MONEY" | "PERCENT" | "COUNT";
}

export interface CubeQueryResponsePayload {
  readonly contract_version: string;
  readonly source: CubeQuerySourcePayload;
  readonly measures: readonly string[];
  readonly grouping: readonly CubeGroupableDimension[];
  readonly period_from: string;
  readonly period_to: string;
  readonly accounting_basis: "STAT" | "GAAP";
  readonly authorized_sections: readonly string[];
  readonly entity_scope: {
    readonly all_entities: boolean;
    readonly entity_codes: readonly string[];
  };
  readonly rows: readonly CubeQueryRowPayload[];
  readonly measure_metadata: Readonly<Record<string, CubeMeasureMetadataPayload>>;
  readonly row_count: number;
  /** Rows withheld by minimum-cohort suppression. Disclosed, never silently dropped. */
  readonly suppressed_row_count: number;
  /** True when row security narrowed the result, so a subtotal is a partial view, not a total. */
  readonly is_scope_limited: boolean;
  readonly scope_limitation_reason: string | null;
  readonly eliminations_proven: boolean;
  /**
   * `entityCode = "CONSOLIDATED"` is stored as a row alongside its own components, so it is
   * excluded when the `entityCode` axis is absent. These two fields are the disclosure that the
   * rule fired and how many rows it removed.
   */
  readonly consolidated_exclusion_applied: boolean;
  readonly excluded_consolidated_rows: number;
}

export interface CubeCatalogMeasurePayload {
  readonly code: string;
  readonly label: string;
  readonly format: "MONEY" | "PERCENT" | "COUNT";
  readonly decimals: number;
  readonly kind: "AMOUNT" | "COUNT" | "RATIO" | "COMPOSITE_RATIO";
  readonly owning_section: string;
  readonly time_aggregation: "FIRST" | "LAST" | "SUM" | "RECOMPUTE_FROM_COMPONENTS";
  readonly summable_across_rows: boolean;
  readonly as_of_dimension: "period" | "valuationPeriod" | null;
  readonly participates_in_elimination: boolean;
  readonly required_gross_ceded_net: "GROSS" | "CEDED" | "NET" | "NA" | null;
  readonly planning_convention: {
    readonly mode: "REQUIRE" | "EXCLUDE";
    readonly value: string;
  };
  readonly component_measure_codes: readonly string[];
}

export interface CubeCatalogResponsePayload {
  readonly contract_version: string;
  readonly measures: readonly CubeCatalogMeasurePayload[];
  readonly groupable_dimensions: readonly CubeGroupableDimension[];
  readonly filterable_dimensions: readonly CubeGroupableDimension[];
  readonly caps: {
    readonly max_measures: number;
    readonly max_grouping: number;
    readonly max_filter_values: number;
    readonly max_period_months: number;
    readonly max_result_cells: number;
    readonly max_source_rows: number;
  };
}

export async function getPlanningCubeCatalog(): Promise<CubeCatalogResponsePayload> {
  const response = await fetch(`${API_BASE}/api/planning/cube/catalog`, {
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as CubeCatalogResponsePayload;
}

export async function queryPlanningCube(
  request: CubeQueryRequestPayload,
): Promise<CubeQueryResponsePayload> {
  const response = await fetch(`${API_BASE}/api/planning/cube/query`, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify(request),
  });
  return (await parseJsonResponse(response)) as CubeQueryResponsePayload;
}

// --- Five-year outlook (LIO-30250 phase 2) ---------------------------------------------------

/**
 * The four outlook routes. Response payloads mirror `apps/planning/api/src/schemas/outlook.ts`
 * field-for-field in snake_case; the PUT body is camelCase under `publication`, because that
 * schema wraps the phase-1 `OutlookAssumptionPublicationSchema` unmodified.
 *
 * `OutlookQuerySchema` is `.strict()`, so an unexpected query parameter is a 400 rather than an
 * ignored one -- the parameter names below are load-bearing and pinned by a test.
 */
export type OutlookDriverKind =
  | "GWP_GROWTH"
  | "OPEX_NON_COMP_GROWTH"
  | "OPEX_COMP_GROWTH"
  | "INVESTMENT_NEW_MONEY_RATE";

export interface OutlookResolvedAssumptionPayload {
  readonly driver_kind: OutlookDriverKind;
  readonly division_code: string;
  /** `null` means AUTO -- FP&A stated nothing and the engine derived `resolved_rate`. */
  readonly rate: string | null;
  readonly resolved_rate: string;
  readonly is_auto: boolean;
  readonly basis: "FY1_TO_FY2" | "NO_PRIOR_BASE";
}

export interface OutlookAssumptionsResponse {
  readonly scenario_id: string;
  readonly contract_version: string;
  readonly formula_version: string;
  readonly snapshot_id: string | null;
  readonly snapshot_checksum: string | null;
  readonly base_fiscal_year: number;
  readonly assumptions: readonly OutlookResolvedAssumptionPayload[];
}

export interface OutlookAssumptionInputBody {
  readonly driverKind: OutlookDriverKind;
  /** Required for `GWP_GROWTH` and forbidden for the three entity-wide drivers. */
  readonly divisionCode?: "RETAIL" | "WHOLESALE" | "PROGRAMS";
  readonly rate: string | null;
}

export interface OutlookAssumptionPublicationBody {
  readonly contractVersion: string;
  readonly formulaVersion: string;
  readonly cycleId: string;
  readonly scenarioId: string;
  readonly assumptions: readonly OutlookAssumptionInputBody[];
}

export interface SaveOutlookAssumptionsResponse {
  readonly scenario_id: string;
  readonly snapshot_id: string;
  readonly snapshot_checksum: string;
  readonly contract_version: string;
  readonly formula_version: string;
  readonly created: boolean;
  readonly assumptions: readonly OutlookResolvedAssumptionPayload[];
}

export interface FiveYearOutlookDivisionPayload {
  readonly division_code: string;
  /** Carried through from the projection, never re-derived from the division code (ADR-058 §9). */
  readonly entity_code: string;
  readonly gross_written_premium: string;
}

export interface FiveYearOutlookEntityPayload {
  readonly entity_code: string;
  readonly beginning_invested_assets: string;
  readonly lines: readonly { readonly line: string; readonly amount: string }[];
}

export interface FiveYearOutlookYearPayload {
  readonly fiscal_year: number;
  readonly year_offset: number;
  readonly is_outlook: true;
  readonly divisions: readonly FiveYearOutlookDivisionPayload[];
  readonly entities: readonly FiveYearOutlookEntityPayload[];
}

export interface FiveYearProfitAndLossResponse {
  readonly scenario_id: string;
  readonly basis: "STAT" | "GAAP";
  readonly base_fiscal_year: number;
  readonly formula_version: string;
  readonly snapshot_id: string | null;
  readonly snapshot_checksum: string | null;
  readonly budget_years: readonly {
    readonly period: string;
    readonly entity_code: string;
    readonly accounting_basis: "STAT" | "GAAP";
    readonly line: string;
    readonly amount: string;
    readonly formula_version: string;
  }[];
  readonly outlook_years: readonly FiveYearOutlookYearPayload[];
}

export interface FiveYearKpiMetricPayload {
  readonly code: string;
  readonly value: string | null;
  /** Empty by construction: an outlook metric traces to nothing persisted (ADR-058 §1). */
  readonly source_fact_ids: readonly string[];
  readonly is_outlook: boolean;
}

export interface FiveYearKpiResponse {
  readonly scenario_id: string;
  readonly basis: "STAT" | "GAAP";
  readonly base_fiscal_year: number;
  readonly formula_version: string;
  readonly years: readonly {
    readonly fiscal_year: number;
    readonly year_offset: number;
    readonly entities: readonly {
      readonly entity_code: string;
      readonly metrics: readonly FiveYearKpiMetricPayload[];
    }[];
  }[];
}

export async function getOutlookAssumptions(
  scenarioId: string,
  basis: "STAT" | "GAAP" = "GAAP",
  entityCode?: string,
): Promise<OutlookAssumptionsResponse> {
  const query = new URLSearchParams({
    basis,
    ...(entityCode === undefined || entityCode === "" ? {} : { entity_code: entityCode }),
  }).toString();
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/outlook/assumptions?${query}`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as OutlookAssumptionsResponse;
}

/** `PUT`, not `POST`: the publication replaces the scenario's assumption set. 201 on a new
 * snapshot, 200 when an identical payload resolves to the existing one. */
export async function saveOutlookAssumptions(
  scenarioId: string,
  publication: OutlookAssumptionPublicationBody,
): Promise<SaveOutlookAssumptionsResponse> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/outlook/assumptions`,
    {
      method: "PUT",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({ publication }),
    },
  );
  return (await parseJsonResponse(response)) as SaveOutlookAssumptionsResponse;
}

export async function getFiveYearProfitAndLoss(
  scenarioId: string,
  basis: "STAT" | "GAAP" = "GAAP",
  entityCode?: string,
): Promise<FiveYearProfitAndLossResponse> {
  const query = new URLSearchParams({
    basis,
    ...(entityCode === undefined || entityCode === "" ? {} : { entity_code: entityCode }),
  }).toString();
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/outlook/five-year-pl?${query}`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as FiveYearProfitAndLossResponse;
}

export async function getFiveYearKpis(
  scenarioId: string,
  basis: "STAT" | "GAAP" = "GAAP",
  entityCode?: string,
): Promise<FiveYearKpiResponse> {
  const query = new URLSearchParams({
    basis,
    ...(entityCode === undefined || entityCode === "" ? {} : { entity_code: entityCode }),
  }).toString();
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/outlook/five-year-kpis?${query}`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as FiveYearKpiResponse;
}

export interface ManagementViewPayload {
  readonly view_id: string;
  readonly name: string;
  readonly basis: "STAT" | "GAAP";
  readonly grain: "MONTH" | "QUARTER" | "YEAR";
  readonly period_from: string | null;
  readonly period_to: string | null;
  readonly metric_codes: readonly string[];
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ManagementViewDefinitionPayload {
  readonly name: string;
  readonly basis: "STAT" | "GAAP";
  readonly grain: "MONTH" | "QUARTER" | "YEAR";
  readonly period_from: string | null;
  readonly period_to: string | null;
  readonly metric_codes: readonly string[];
}

export async function getManagementViews(): Promise<readonly ManagementViewPayload[]> {
  const response = await fetch(`${API_BASE}/api/planning/management-views`, {
    headers: authHeaders(),
  });
  const payload = (await parseJsonResponse(response)) as {
    views: readonly ManagementViewPayload[];
  };
  return payload.views;
}

export async function createManagementView(
  definition: ManagementViewDefinitionPayload,
): Promise<ManagementViewPayload> {
  const response = await fetch(`${API_BASE}/api/planning/management-views`, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify(definition),
  });
  const payload = (await parseJsonResponse(response)) as { view: ManagementViewPayload };
  return payload.view;
}

export async function updateManagementView(
  viewId: string,
  definition: ManagementViewDefinitionPayload,
): Promise<ManagementViewPayload> {
  const response = await fetch(
    `${API_BASE}/api/planning/management-views/${encodeURIComponent(viewId)}`,
    {
      method: "PUT",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(definition),
    },
  );
  const payload = (await parseJsonResponse(response)) as { view: ManagementViewPayload };
  return payload.view;
}

export async function deleteManagementView(viewId: string): Promise<void> {
  const response = await fetch(
    `${API_BASE}/api/planning/management-views/${encodeURIComponent(viewId)}`,
    { method: "DELETE", headers: authHeaders() },
  );
  await parseJsonResponse(response);
}

export interface PlanningLockHistoryPayload {
  readonly scenario_id: string;
  readonly source_locked_version: {
    readonly id: string;
    readonly name: string;
    readonly saved_at: string | null;
  };
  readonly actuals_cutover_period: string | null;
  readonly summary: {
    readonly source_fact_count: number;
    readonly current_fact_count: number;
    readonly compared_count: number;
    readonly added_count: number;
    readonly removed_count: number;
    readonly changed_count: number;
    readonly unchanged_count: number;
    readonly source_amount: string;
    readonly current_amount: string;
    readonly difference_amount: string;
  };
  readonly rows: readonly {
    readonly coordinate_id: string;
    readonly status: "ADDED" | "REMOVED" | "CHANGED" | "UNCHANGED";
    readonly section: string;
    readonly scope_type: "GLOBAL" | "ENTITY" | "DIVISION" | "COST_CENTER";
    readonly scope_key: string;
    readonly period: string;
    readonly division_code: string;
    readonly cost_center_code: string;
    readonly product_code: string;
    readonly lob_code: string;
    readonly entity_code: string;
    readonly state_code: string;
    readonly restricted_coordinate: boolean;
    readonly prior_amount: string | null;
    readonly current_amount: string | null;
    readonly difference_amount: string;
    readonly difference_percent: string | null;
  }[];
  readonly pagination: {
    readonly page: number;
    readonly page_size: number;
    readonly total_rows: number;
    readonly total_pages: number;
  };
}

export async function getPlanningLockHistory(
  scenarioId: string,
  input: { readonly changedOnly: boolean; readonly page?: number; readonly pageSize?: number },
): Promise<PlanningLockHistoryPayload> {
  const query = new URLSearchParams({
    changed_only: String(input.changedOnly),
    page: String(input.page ?? 1),
    page_size: String(input.pageSize ?? 100),
  });
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/lock-history?${query.toString()}`,
    { headers: authHeaders() },
  );
  return (await parseJsonResponse(response)) as PlanningLockHistoryPayload;
}

export interface PlanningExportDownload {
  readonly blob: Blob;
  readonly filename: string;
  readonly contentType: string;
}

export async function downloadPlanningExport(input: {
  readonly planType: "budget" | "forecast";
  readonly fiscalYear: number;
  readonly cutoffMonth?: number;
  readonly versionId?: string;
  readonly entityCode?: string;
  readonly format: "csv" | "xlsx";
}): Promise<PlanningExportDownload> {
  const path =
    input.planType === "forecast"
      ? `/api/planning/exports/forecast/${input.fiscalYear}/${input.cutoffMonth ?? ""}`
      : `/api/planning/exports/budget/${input.fiscalYear}`;
  const query = new URLSearchParams({ format: input.format });
  if (input.versionId !== undefined) query.set("version_id", input.versionId);
  if (input.entityCode !== undefined) query.set("entity_code", input.entityCode);
  const response = await fetch(`${API_BASE}${path}?${query.toString()}`, {
    headers: authHeaders({
      accept:
        input.format === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "text/csv",
    }),
  });
  if (!response.ok) {
    await parseJsonResponse(response);
    throw new Error(`Planning export failed with ${response.status}`);
  }
  const contentType = response.headers.get("content-type") ?? "application/octet-stream";
  const fallback = `aire-${input.planType}-${input.fiscalYear}.${input.format}`;
  return {
    blob: await response.blob(),
    filename: downloadFilename(response.headers.get("content-disposition"), fallback),
    contentType,
  };
}

export interface RecomputeFinancialStatementsResponse {
  readonly scenario_id: string;
  readonly financial_driver_snapshot_id: string;
  readonly source_version: string;
  readonly formula_version: string;
  readonly period_from: string;
  readonly period_to: string;
  readonly fact_count: number;
  readonly control_count: number;
  readonly investment_class_result_count: number;
}

export async function recomputeFinancialStatements(
  scenarioId: string,
  snapshotId: string,
): Promise<RecomputeFinancialStatementsResponse> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/financial-statements/recompute`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({ financial_driver_snapshot_id: snapshotId, background: true }),
    },
  );
  const initial = await parseJsonResponse(response);
  if (response.status !== 202) return initial as RecomputeFinancialStatementsResponse;
  const pendingSchema = z
    .object({
      job_id: z.string().uuid(),
      status: z.enum(["QUEUED", "RUNNING", "COMPLETE", "FAILED"]),
      retry_after_ms: z.number().int().positive().max(10000),
      error: z.string().optional(),
      result: z
        .object({
          scenario_id: z.string().uuid(),
          financial_driver_snapshot_id: z.string().uuid(),
          source_version: z.string().min(1),
          formula_version: z.string().min(1),
          period_from: z.string(),
          period_to: z.string(),
          fact_count: z.number().int().nonnegative(),
          control_count: z.number().int().nonnegative(),
          investment_class_result_count: z.number().int().nonnegative(),
        })
        .strict()
        .optional(),
    })
    .strict();
  let job = pendingSchema.parse(initial);
  const jobId = job.job_id;
  const deadline = Date.now() + 11 * 60_000;
  while (job.status === "QUEUED" || job.status === "RUNNING") {
    if (Date.now() >= deadline)
      throw new Error(
        "Calculation is still running. Return to Financial Statements to check the results.",
      );
    await new Promise((resolve) => setTimeout(resolve, job.retry_after_ms));
    const poll = await fetch(
      `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/financial-statements/jobs/${encodeURIComponent(jobId)}`,
      { headers: authHeaders() },
    );
    job = pendingSchema.parse(await parseJsonResponse(poll));
    if (job.job_id !== jobId)
      throw new Error("Statement calculation status did not match the requested job.");
  }
  if (job.status === "FAILED")
    throw new Error(job.error ?? "Statement calculation failed. Generate again.");
  if (
    !job.result ||
    job.result.scenario_id !== scenarioId ||
    job.result.financial_driver_snapshot_id !== snapshotId
  )
    throw new Error("Statement calculation returned an unexpected result.");
  return job.result;
}

export async function calculateFinancialStatementsFromScenarioBasis(
  scenarioId: string,
  input: {
    readonly effectiveTaxRate: string;
    readonly recognizeLossBenefit: boolean;
  },
): Promise<RecomputeFinancialStatementsResponse> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/financial-statements/scenario-basis`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({
        effective_tax_rate: input.effectiveTaxRate,
        recognize_loss_benefit: input.recognizeLossBenefit,
        prepare_only: true,
      }),
    },
  );
  // Preparing opening-book lineage and articulating statements are separate HTTP requests so
  // each has its own API deadline. The server rechecks the current opening basis on calculation.
  const prepared = z
    .object({ financial_driver_snapshot_id: z.string().uuid() })
    .strict()
    .parse(await parseJsonResponse(response));
  return recomputeFinancialStatements(scenarioId, prepared.financial_driver_snapshot_id);
}

async function parseJsonResponse(response: Response): Promise<unknown> {
  const rawBody = await response.text();
  let payload: unknown;
  try {
    payload = rawBody.length === 0 ? null : (JSON.parse(rawBody) as unknown);
  } catch {
    const contentType = response.headers.get("content-type") ?? "unknown content type";
    throw new Error(
      `Planning API returned a non-JSON response (${response.status}, ${contentType}). ` +
        "The request may have been routed to the web application instead of the API.",
    );
  }
  if (!response.ok) {
    throw planningApiErrorFromPayload(payload, response.status);
  }
  return payload;
}

export async function getOpeningBookCatalog(
  scenarioId: string,
  valuationDate: string,
  options: FiarCatalogOptions = {},
) {
  return OpeningBookCatalogSchema.parse(
    await pollFiarCatalog(async (signal, retry) => {
      const query = new URLSearchParams({
        view: "catalog",
        valuationDate,
        ...(retry ? { retry: "true" } : {}),
      });
      const response = await fetch(
        `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/premium-input-facts/opening-book?${query}`,
        { headers: authHeaders(), signal },
      );
      return parseJsonResponse(response);
    }, options),
  );
}

export async function startOpeningBookLoad(
  scenarioId: string,
  request: {
    companyCode: string;
    divisionCode: string;
    productCode: string;
    valuationDate: string;
    unmappedSource?: { divisionCode: string | null; productCode: string | null };
  },
  signal?: AbortSignal,
): Promise<string> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/premium-input-facts/opening-book`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({ action: "LOAD", ...request }),
      ...(signal === undefined ? {} : { signal }),
    },
  );
  return z
    .object({ snapshotId: z.string().uuid(), status: z.literal("LOADING") })
    .parse(await parseJsonResponse(response)).snapshotId;
}
export async function getOpeningBookPreview(
  scenarioId: string,
  snapshotId: string,
): Promise<OpeningBookPreview> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/premium-input-facts/opening-book?snapshotId=${encodeURIComponent(snapshotId)}`,
    { headers: authHeaders() },
  );
  return OpeningBookPreviewSchema.parse(await parseJsonResponse(response));
}
export async function acceptOpeningBook(
  scenarioId: string,
  preview: OpeningBookPreview,
): Promise<OpeningBookPreview> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/premium-input-facts/opening-book`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({
        action: "PUBLISH",
        snapshotId: preview.snapshotId,
        bridgeChecksum: preview.bridgeChecksum,
        expectedPublishedId: preview.expectedPublishedId,
        acceptBridge: true,
        ...(preview.unmappedSource ? { acceptUnallocated: true } : {}),
      }),
    },
  );
  return OpeningBookPreviewSchema.parse(await parseJsonResponse(response));
}
export async function listOpeningBooks(scenarioId: string) {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/premium-input-facts/opening-book`,
    { headers: authHeaders() },
  );
  return z
    .object({
      snapshots: z.array(
        z.object({
          id: z.string().uuid(),
          companyCode: z.string(),
          divisionCode: z.string(),
          productCode: z.string(),
          status: z.string(),
          valuationDate: z.string(),
          openingRequirement: z
            .enum(["OPENING_BOOK_REQUIRED", "ZERO_RUNOFF_CONFIRMED"])
            .default("OPENING_BOOK_REQUIRED"),
        }),
      ),
    })
    .parse(await parseJsonResponse(response)).snapshots;
}

function isFiarJobResponse(value: unknown): value is FiarPriorYearBasisJobResponse {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<FiarPriorYearBasisJobResponse>;
  return (
    typeof candidate.job_id === "string" &&
    (candidate.status === "QUEUED" || candidate.status === "RUNNING") &&
    typeof candidate.retry_after_ms === "number"
  );
}

function downloadFilename(contentDisposition: string | null, fallback: string): string {
  const match = contentDisposition?.match(/filename="([^"\\/]+)"/u);
  return match?.[1] ?? fallback;
}

function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => globalThis.setTimeout(resolve, milliseconds));
}

const EnterpriseExpenseReportSchema = z.object({
  status: z.enum(["EMPTY", "NEEDS_ALLOCATION", "CURRENT", "STALE"]),
  sourceAmount: z.number().finite(),
  allocatedAmount: z.number().finite(),
  runId: z.string().uuid().nullable(),
  policySnapshotId: z.string().uuid().nullable(),
  policyVersion: z.string().nullable(),
  companyTotals: z
    .array(z.object({ entityCode: z.enum(["LIC", "LSIC"]), amount: z.number().finite() }))
    .default([]),
  facts: z.array(
    z.object({
      id: z.string().uuid(),
      sourceFactId: z.string().uuid(),
      component: z.enum(["OPERATING_EXPENSE", "COMPENSATION"]).default("OPERATING_EXPENSE"),
      period: z.string().regex(/^\d{4}-(?:0[1-9]|1[0-2])$/u),
      entityCode: z.enum(["LIC", "LSIC"]),
      costCenterCode: z.string(),
      accountCode: z.string(),
      supplierCode: z.string(),
      divisionCode: z.enum(["RETAIL", "WHOLESALE", "PROGRAMS"]),
      productCode: z.string(),
      amount: z.number().finite(),
    }),
  ),
});
export type EnterpriseExpenseReport = z.infer<typeof EnterpriseExpenseReportSchema>;
export async function getEnterpriseExpenses(
  scenarioId: string,
  year?: number,
): Promise<EnterpriseExpenseReport> {
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/opex-allocation/enterprise${year === undefined ? "" : `?fiscal_year=${year}`}`,
    { headers: authHeaders() },
  );
  return EnterpriseExpenseReportSchema.parse(await parseJsonResponse(response));
}
export async function allocateEnterpriseExpenses(
  scenarioId: string,
  policyId: string,
  year: number,
): Promise<EnterpriseExpenseReport> {
  const body = z
    .object({
      policy_snapshot_id: z.string().uuid(),
      fiscal_year: z.number().int().min(2000).max(2200),
    })
    .strict()
    .parse({ policy_snapshot_id: policyId, fiscal_year: year });
  const response = await fetch(
    `${API_BASE}/api/planning/scenarios/${encodeURIComponent(scenarioId)}/opex-allocation/enterprise`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(body),
    },
  );
  return EnterpriseExpenseReportSchema.parse(await parseJsonResponse(response));
}
